import useUserUiPreferenceStorageValue from "../hooks/useUserUiPreferenceStorageValue.js";
import { userUiPreferenceStorage } from "../lib/userUiPreferenceStorage.js";
import React from "react";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useNavigation,
} from "react-router";
import "./AppShell.css";
import ResponsiveNavigation from "./ResponsiveNavigation.jsx";
import useMediaQuery, { COMPACT_SHELL_QUERY } from "../hooks/useMediaQuery.js";
import {
  AlertTriangle,
  BarChart2,
  Camera,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock3,
  Database,
  DollarSign,
  GitBranch,
  LayoutDashboard,
  Layers,
  LoaderCircle,
  MapPin,
  Menu,
  LogOut,
  FileText,
  PanelLeftClose,
  PanelLeftOpen,
  Shield,
  SlidersHorizontal,
  Truck,
  Upload,
  Wrench,
  Users,
  X,
} from "lucide-react";
import {
  ActionButton,
  HoverCard,
  IconContainer,
  joinClasses,
} from "./ResultsPrimitives.jsx";
import { BRAND_LOGO_SRC, BRAND_IA_SRC, BRAND_NAME } from "./BrandLogo.jsx";
import DispatchColorIntensityControl from "./DispatchColorIntensityControl.jsx";
import PageSurfaceHeader from "./PageSurfaceHeader.jsx";
import ResultsHeaderLoadingRail from "./ResultsHeaderLoadingRail.jsx";
import { RightPanel } from "./RightPanel.jsx";
import useShellData from "../hooks/useShellData.js";
import useShellNavigation from "../hooks/useShellNavigation.js";
import useProductUpdates from "../hooks/useProductUpdates.js";
import useShellStatus from "../hooks/useShellStatus.js";
import { useOptimizationJobTracker } from "../hooks/useOptimizationJobTracker.js";
import {
  buildResultsRightPanelData,
  getResultsServiceValidationSnapshot,
  subscribeResultsRightPanel,
} from "../utils/rightPanelData.js";
import {
  buildFallbackNavSections,
  getNavItemKey,
  getNavSubItems,
  getPathnameNavKey,
  getShellHeaderFallback,
  hasActiveNavItem,
  injectAdminNavSection,
  injectLocalToolsNavItem,
  injectAutomationsNavItem,
  injectBillingNavSection,
  injectDriverAccountsNavItem,
  injectPortalNavItem,
  injectProductUpdatesNavItem,
  resolveNavItemHoverDescription,
  resolveNavSectionHoverDescription,
} from "../utils/appShellNavigation.js";
import {
  canAccessAdminPage,
  canAccessLocalToolsPage,
  canAccessConsolidationPage,
  canAccessGeotabPage,
  canAccessBillingPage,
  canAccessDriverPortal,
  canManageDriverAccounts,
  canManageAutomations,
  canManageSystemDiagnostics,
  canPublishDriverPortal,
  filterNavSectionsByAccess,
  isBillingWorkspaceOnly,
} from "../lib/accessControl.js";
import { enrichResultsUrlWithStoredDispatchContext } from "../utils/dispatchDateUrls.js";
import {
  resolveAppShellFrameClassName,
  resolvePageSurfaceWidthClassName,
} from "../utils/pageSurfaceLayout.js";
import useAccess from "../hooks/useAccess.jsx";
import PreferencesDelegationBanner from "./PreferencesDelegationBanner.jsx";
import useDiagnosticsAutoSnapshots from "../hooks/useDiagnosticsAutoSnapshots.js";
import useDispatchAlertCapture from "../hooks/useDispatchAlertCapture.js";
import useDispatchAlertCaptureConfigActive from "../hooks/useDispatchAlertCaptureConfigActive.js";
import { serializeDispatchAlertCaptureContext } from "../lib/dispatchAlertCaptureContext.js";
import useReassignmentDiagnosticsHistoryActive from "../hooks/useReassignmentDiagnosticsHistoryActive.js";
import {
  dispatchResultsShellNavigation,
  getResultsDefaultNavKey,
} from "../utils/resultsDetailViewNavigation.js";
import { formatOptimizationStatusTime } from "../utils/appShellStatus.js";
import { renderInlineNoBreakIdentifiers } from "../utils/inlineMessageFormatting.js";
import OptimizationProgressCard, { isFleetOptimizationProgressCardVisible } from "./OptimizationProgressCard.jsx";
import ProductUpdateBanner from "./ProductUpdateBanner.jsx";
import ProductUpdateAnnouncement from "./ProductUpdateAnnouncement.jsx";
import TodayStalePortalRoutesBanner from "./TodayStalePortalRoutesBanner.jsx";
import NotificationCenterButton from "./NotificationCenterButton.jsx";
import AskEpiButton from "./epi/AskEpiButton.jsx";
import useTodayStalePortalRoutesAlert from "../hooks/useTodayStalePortalRoutesAlert.js";
import {
  getFleetFullscreenOverlaySnapshot,
  subscribeFleetFullscreenOverlay,
} from "../utils/fleetFullscreenOverlayState.js";

const RESULTS_THEMED_PAGE_KINDS = new Set(["admin", "automations", "billing", "dashboard", "driver_accounts", "home", "orders", "import", "reports", "data_manager", "system_config", "results", "updates", "loading_preview", "geotab"]);
const RIGHT_PANEL_COLLAPSED_STORAGE_KEY = "grain.rightPanel.collapsed";

function readStoredRightPanelCollapsed() {
  try {
    return userUiPreferenceStorage("local")?.getItem(RIGHT_PANEL_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function storeRightPanelCollapsed(collapsed) {
  try {
    userUiPreferenceStorage("local")?.setItem(RIGHT_PANEL_COLLAPSED_STORAGE_KEY, collapsed ? "true" : "false");
  } catch {
    // Préférence d'affichage seulement : sans stockage, le panneau reste ouvert.
  }
}

// Dernière identité reçue : le bloc du menu reste affiché pendant qu'une page charge sa coquille.
const SHELL_BRAND_STORAGE_KEY = "grain.shell.brand";

function readStoredShellBrand() {
  try {
    const storedBrand = JSON.parse(window.localStorage.getItem(SHELL_BRAND_STORAGE_KEY) || "null");
    return storedBrand && typeof storedBrand === "object" ? storedBrand : {};
  } catch {
    return {};
  }
}

function storeShellBrand(brand) {
  try {
    window.localStorage.setItem(SHELL_BRAND_STORAGE_KEY, JSON.stringify(brand));
  } catch {
    // Sans stockage, le bloc apparaît dès que la coquille est chargée.
  }
}

const ICON_MAP = {
  admin: <Shield size={17} />,
  automations: <Clock3 size={17} />,
  "local-tools": <Wrench size={17} />,
  billing: <DollarSign size={17} />,
  consolidation: <Layers size={17} />,
  dashboard: <LayoutDashboard size={17} />,
  "driver-accounts": <Users size={17} />,
  portal: <Truck size={17} />,
  updates: <ClipboardList size={17} />,
  orders: <ClipboardList size={17} />,
  import: <Upload size={17} />,
  reports: <FileText size={17} />,
  "data-manager": <Database size={17} />,
  "system-config": <SlidersHorizontal size={17} />,
  "dispatch-captures": <Camera size={17} />,
  trucks: <Truck size={17} />,
  affectation: <GitBranch size={17} />,
  geotab: <MapPin size={17} />,
  results: <BarChart2 size={17} />,
  errors: <AlertTriangle size={17} />,
};

const DISPATCH_CAPTURE_BADGE_CLASS_NAME = "inline-flex h-[30px] min-h-[30px] shrink-0 items-center gap-1.5 rounded-[4px] border border-slate-300 bg-white px-2.5 text-[10px] font-semibold text-slate-700 shadow-none transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2";
const HEADER_ACTION_BASE_CLASS_NAME = "inline-flex min-h-[30px] items-center justify-center gap-1.5 whitespace-nowrap rounded-[6px] border px-[0.7rem] py-[0.32rem] text-[0.71rem] font-extrabold tracking-[0.01em] shadow-header-action transition-[background-color,border-color,box-shadow,color] duration-200 hover:shadow-header-action-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-300/60";

const HEADER_ACTION_TONE_CLASS_NAMES = {
  neutral: "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:border-slate-400 focus-visible:text-slate-900",
  soft: "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:border-slate-400 focus-visible:text-slate-900",
};

const HEADER_INDICATOR_TONE_CLASS_NAMES = {
  danger: "border-border-header-danger bg-danger-soft",
  info: "border-border-header-info bg-slate-100",
  neutral: "border-border-header-neutral bg-[var(--panel-soft)]",
  success: "border-border-header-success bg-[var(--grain-badge-bg)]",
  warning: "border-border-header-warning bg-amber-soft",
};

const SUB_NAV_ACTIVE_CLASS_NAME = "is-active";

const RESULTS_HEADER_ACCENT_CLASS_NAMES = {
  dot: "bg-shell-header-accent-dot",
  dotStrong: "bg-shell-header-accent-dot-strong",
  rail: "bg-shell-header-accent-rail",
  railStrong: "bg-shell-header-accent-rail-strong",
  solid: "bg-kpi-accent",
};

const RESULTS_HEADER_STATUS_TONE_CLASS_NAMES = {
  danger: {
    dot: "bg-danger",
    text: "text-shell-status-danger-ink",
  },
  neutral: {
    dot: "bg-slate-400",
    text: "text-slate-600",
  },
  success: {
    dot: "bg-kpi-accent",
    text: "text-shell-status-success-ink",
  },
  warning: {
    dot: "bg-shell-status-warning-dot",
    text: "text-shell-status-warning-ink",
  },
};

const OPTIMIZATION_NOTICE_TONE_CLASS_NAMES = {
  cancelled: {
    icon: "border-[rgba(208,139,62,0.18)] bg-white text-shell-status-warning-ink",
    pill: "border-[rgba(208,139,62,0.18)] bg-white text-shell-status-warning-ink",
    shell: "border-[rgba(208,139,62,0.18)] bg-[var(--shell-notice-warning-fill)]",
  },
  cancelling: {
    icon: "border-shell-notice-running-icon-border bg-white text-slate-600",
    pill: "border-shell-notice-running-pill-border bg-white text-slate-700",
    shell: "border-shell-notice-running-border bg-slate-50",
  },
  danger: {
    icon: "border-shell-notice-danger-icon-border bg-white text-shell-notice-danger-ink",
    pill: "border-shell-notice-danger-pill-border bg-white text-shell-notice-danger-ink",
    shell: "border-shell-notice-danger-border bg-[var(--shell-notice-danger-fill)]",
  },
  running: {
    icon: "border-shell-notice-running-icon-border bg-white text-slate-600",
    pill: "border-shell-notice-running-pill-border bg-white text-slate-700",
    shell: "border-shell-notice-running-border bg-slate-50",
  },
  success: {
    icon: "border-shell-notice-success-icon-border bg-white text-shell-notice-success-ink",
    pill: "border-shell-notice-success-pill-border bg-white text-shell-notice-success-ink",
    shell: "border-shell-notice-success-border bg-[var(--shell-notice-success-fill)]",
  },
};

function resolveOptimizationNoticeTone(notice) {
  if (!notice) {
    return OPTIMIZATION_NOTICE_TONE_CLASS_NAMES.running;
  }

  if (notice.status === "running") {
    return OPTIMIZATION_NOTICE_TONE_CLASS_NAMES.running;
  }

  if (notice.status === "cancelling") {
    return OPTIMIZATION_NOTICE_TONE_CLASS_NAMES.cancelling;
  }

  if (notice.status === "cancelled" || notice.status === "partial") {
    return OPTIMIZATION_NOTICE_TONE_CLASS_NAMES.cancelled;
  }

  if (notice.status === "completed") {
    return OPTIMIZATION_NOTICE_TONE_CLASS_NAMES.success;
  }

  return OPTIMIZATION_NOTICE_TONE_CLASS_NAMES.danger;
}

function isDangerOptimizationNotice(notice) {
  return resolveOptimizationNoticeTone(notice) === OPTIMIZATION_NOTICE_TONE_CLASS_NAMES.danger;
}

function MinimizedStatusNoticeStrip({ className = "", hasRightPanel = false, notice, onExpand, style = undefined }) {
  if (!notice) {
    return null;
  }

  const noticeTone = resolveOptimizationNoticeTone(notice);

  return (
    <button
      type="button"
      onClick={onExpand}
      className={joinClasses([
        "pointer-events-auto absolute right-0 z-[calc(var(--z-index-modal)+11)] w-2.5 rounded-l-[10px] border border-r-0 shadow-notice-shell",
        "transition-[width,box-shadow] duration-200 hover:w-3 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-300",
        hasRightPanel ? "min-[1281px]:right-[var(--shell-right-panel-width)]" : "",
        noticeTone.shell,
        className,
      ])}
      style={style}
      aria-label={`Afficher les détails : ${notice.title || ""}`}
      title={notice.title || "Afficher le bandeau de statut"}
    />
  );
}

const OPTIMIZATION_NOTICE_ACTIVITY_TONE_CLASS_NAMES = {
  danger: "border-shell-notice-danger-pill-border bg-white text-shell-notice-danger-ink",
  neutral: "border-line-soft bg-white text-slate-700",
  success: "border-shell-notice-success-pill-border bg-white text-shell-notice-success-ink",
  warning: "border-shell-notice-running-pill-border bg-white text-slate-700",
};

const OPTIMIZATION_NOTICE_ACTIVITY_HOVER_ACCENT_COLORS = {
  danger: "var(--hovercard-accent-danger)",
  neutral: "var(--hovercard-accent-neutral)",
  success: "var(--hovercard-accent-success)",
  warning: "var(--hovercard-accent-warning)",
};
const MAIN_NAV_HOVERCARD_DELAY_MS = 1000;

const APP_SHELL_CLASS_NAMES = {
  groupOpenSurface: "border-line bg-white",
  groupToggleActive: "border-accent bg-accent text-white",
  headerEyebrow: "text-shell-eyebrow tracking-[0.18em]",
  pendingActionText: "text-shell-action-soft-ink",
  focusRing: "focus-visible:ring-shell-focus-ring",
};

function getInteractiveDataProps(item) {
  if (!item || typeof item !== "object") {
    return {};
  }

  return {
    "data-detail-force-open": item.detail_force_open ? "true" : undefined,
    "data-detail-view-link": item.detail_view_link || undefined,
    "data-errors-open-all": item.errors_open_all ? "true" : undefined,
  };
}

function getNavSectionSlug(section, index = 0) {
  const label = typeof section?.label === "string" ? section.label : "";
  const slug = label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");

  return slug || `nav-section-${index + 1}`;
}

function isInternalRouteHref(href) {
  return typeof href === "string" && href.startsWith("/");
}

function NavigationHoverCardContent({ eyebrow = "Menu principal", description = "", detail = null }) {
  if (!description && !detail) {
    return null;
  }

  return (
    <div className="grid gap-1.5">
      <p className="m-0 text-[0.68rem] font-black uppercase tracking-[0.16em] text-slate-500">{eyebrow}</p>
      {description ? <p className="m-0 text-sm leading-6 text-slate-600">{description}</p> : null}
      {detail ? <p className="m-0 text-[12px] leading-5 text-slate-500">{detail}</p> : null}
    </div>
  );
}

function buildNavigationHoverEyebrow({ parentLabel = "", sectionLabel = "" }) {
  if (parentLabel) {
    return `Sous-menu · ${parentLabel}`;
  }

  if (sectionLabel) {
    return `Section · ${sectionLabel}`;
  }

  return "Menu principal";
}

function buildNavigationHoverDetail({ disabledReason = "", parentLabel = "", sectionLabel = "", subItemCount = 0 }) {

  if (subItemCount > 0 && sectionLabel) {
    const detail = `${subItemCount} sous-entrée${subItemCount > 1 ? "s" : ""} dans ${sectionLabel}.`;
    return disabledReason ? `${detail} ${disabledReason}` : detail;
  }

  if (subItemCount > 0) {
    const detail = `${subItemCount} sous-entrée${subItemCount > 1 ? "s" : ""}.`;
    return disabledReason ? `${detail} ${disabledReason}` : detail;
  }

  if (parentLabel) {
    const detail = sectionLabel ? `Sous-vue de ${parentLabel} dans ${sectionLabel}.` : `Sous-vue de ${parentLabel}.`;
    return disabledReason ? `${detail} ${disabledReason}` : detail;
  }

  if (sectionLabel) {
    const detail = `Section ${sectionLabel}.`;
    return disabledReason ? `${detail} ${disabledReason}` : detail;
  }

  return disabledReason || null;
}

function SubNavItem({ item, onNavigate = undefined, parentLabel = "", sectionLabel = "" }) {
  if (!item || typeof item !== "object") {
    return null;
  }

  const isActive = Boolean(item.is_active || item.aria_current);
  const isDisabled = item.is_disabled === true;
  const disabledReason = typeof item.disabled_reason === "string" ? item.disabled_reason.trim() : "";
  const interactiveDataProps = getInteractiveDataProps(item);
  const hasDetailViewLink = Boolean(item.detail_view_link);
  const hoverDescription = resolveNavItemHoverDescription(item);
  const hoverCardContent = hoverDescription ? (
    <NavigationHoverCardContent
      eyebrow={buildNavigationHoverEyebrow({ parentLabel, sectionLabel })}
      description={hoverDescription}
      detail={buildNavigationHoverDetail({ disabledReason, parentLabel, sectionLabel })}
    />
  ) : null;

  const className = joinClasses([
    "shell-subnav-item flex! min-h-8! items-center! gap-2! rounded-[4px]! border! border-transparent! pl-7! pr-3! py-1.5! text-sm! font-medium! tracking-[0.01em]! transition-colors duration-150",
    isDisabled
      ? "cursor-not-allowed opacity-60"
      : isActive
      ? SUB_NAV_ACTIVE_CLASS_NAME
      : "",
  ]);

  const dot = (
    <span
      className={joinClasses([
        "h-1.75 w-1.75 shrink-0 rounded-none bg-current",
      ])}
    />
  );

  // Detail-view sub-items stay on React Router for cross-page nav and let onNavigate
  // intercept same-page results transitions when needed.
  let node = null;

  if (isDisabled) {
    node = (
      <div
        aria-disabled="true"
        data-nav-disabled="true"
        {...interactiveDataProps}
        className={className}
      >
        {dot}
        {item.label}
      </div>
    );
  } else if (hasDetailViewLink) {
    if (isInternalRouteHref(item.href)) {
      node = (
        <Link
          to={item.href}
          onClick={(event) => onNavigate?.(event, item)}
          aria-current={item.aria_current ? "page" : undefined}
          {...interactiveDataProps}
          className={className}
        >
          {dot}
          {item.label}
        </Link>
      );
    } else {
      node = (
        <a
          href={item.href}
          onClick={(event) => onNavigate?.(event, item)}
          aria-current={item.aria_current ? "page" : undefined}
          {...interactiveDataProps}
          className={className}
        >
          {dot}
          {item.label}
        </a>
      );
    }
  } else {
    node = (
      <NavLink
        to={item.href || "/"}
        end
        aria-current={item.aria_current ? "page" : undefined}
        {...interactiveDataProps}
        className={className}
      >
        {dot}
        {item.label}
      </NavLink>
    );
  }

  return hoverCardContent ? (
    <HoverCard title={item.label} content={hoverCardContent} placement="right" maxWidth={340} hoverOnly delay={MAIN_NAV_HOVERCARD_DELAY_MS}>
      {node}
    </HoverCard>
  ) : node;
}

function HeaderAction({ action }) {
  if (!action || typeof action !== "object") {
    return null;
  }

  const tone = action.tone === "soft"
    ? HEADER_ACTION_TONE_CLASS_NAMES.soft
    : HEADER_ACTION_TONE_CLASS_NAMES.neutral;
  const className = joinClasses([HEADER_ACTION_BASE_CLASS_NAME, tone]);
  if (action.kind === "link" && typeof action.href === "string" && action.href.trim()) {
    if (isInternalRouteHref(action.href)) {
      return (
        <Link className={className} to={action.href}>
          {action.label}
        </Link>
      );
    }

    return (
      <a className={className} href={action.href}>
        {action.label}
      </a>
    );
  }

  return <span className={className}>{action.label}</span>;
}

function HeaderIndicator({ indicator }) {
  if (!indicator || typeof indicator !== "object") {
    return null;
  }

  const label = typeof indicator.label === "string" ? indicator.label.trim() : "";
  const value = typeof indicator.value === "string" ? indicator.value.trim() : "";

  if (!label && !value) {
    return null;
  }

  const toneKey = typeof indicator.tone === "string" ? indicator.tone : "neutral";
  const toneClassName = HEADER_INDICATOR_TONE_CLASS_NAMES[toneKey] || HEADER_INDICATOR_TONE_CLASS_NAMES.neutral;

  return (
    <div
      className={joinClasses([
        "inline-flex min-h-8 min-w-28 flex-col justify-center rounded-panel border px-2.5 py-1 shadow-inset-soft",
        toneClassName,
      ])}
    >
      {label ? (
        <span className="whitespace-nowrap text-[8px] font-extrabold uppercase tracking-[0.08em] text-slate-500">
          {label}
        </span>
      ) : null}
      {value ? (
        <span className="mt-0.5 text-[12px] font-semibold leading-none text-slate-900">
          {value}
        </span>
      ) : null}
    </div>
  );
}

function ResultsHeaderLead({
  isNavigationPending = false,
  sidebarCollapsed = false,
}) {
  return (
    <NavLink
      className={joinClasses([
        "inline-flex min-w-0 shrink-0 items-center transition-opacity duration-150 hover:opacity-90",
        sidebarCollapsed ? "justify-center" : "justify-start",
      ])}
      to="/accueil"
      aria-label="Retour à l'accueil TransIA"
    >
      <span
        className={joinClasses([
          "shell-brand-logo",
          sidebarCollapsed ? "shell-brand-logo--collapsed" : "",
          isNavigationPending ? "opacity-80" : "",
        ])}
      >
        {sidebarCollapsed ? (
          <img src={BRAND_IA_SRC} alt={BRAND_NAME} draggable={false} />
        ) : (
          <span className="shell-brand-logo__wordmark">
            <img src={BRAND_LOGO_SRC} alt={BRAND_NAME} width={535} height={165} draggable={false} />
          </span>
        )}
      </span>
    </NavLink>
  );
}

function ResultsHeaderTrailAccent({
  className = "",
  disabled = false,
  isActive = false,
  onDotClick = null,
}) {
  const dotClassName = joinClasses([
    "h-1.25 w-1.25 shrink-0 rounded-full",
    RESULTS_HEADER_ACCENT_CLASS_NAMES.solid,
  ]);

  return (
    <span className={joinClasses(["inline-flex h-7 items-center gap-1.5", className])}>
      {typeof onDotClick === "function" ? (
        <button
          type="button"
          className={joinClasses([
            "results-header-loading-dot-trigger",
            dotClassName,
            disabled ? "cursor-not-allowed opacity-45" : "cursor-pointer",
          ])}
          onClick={onDotClick}
          disabled={disabled}
          aria-pressed={isActive}
          aria-label={isActive ? "Arrêter l'aperçu du chargement" : "Prévisualiser l'animation de chargement"}
          title={isActive ? "Arrêter l'aperçu" : "Prévisualiser l'animation de chargement"}
        />
      ) : (
        <span className={dotClassName} aria-hidden="true" />
      )}
      <span className={joinClasses(["h-0.75 flex-1 rounded-full", RESULTS_HEADER_ACCENT_CLASS_NAMES.rail])} aria-hidden="true" />
      <span className={joinClasses(["h-1 w-1 rounded-full", RESULTS_HEADER_ACCENT_CLASS_NAMES.dot])} aria-hidden="true" />
    </span>
  );
}

function ResultsHeaderAccent({ className = "", variant = "trail" }) {
  if (variant === "stack") {
    return (
      <span className={joinClasses(["inline-flex h-7 items-center gap-1.5", className])} aria-hidden="true">
        <span className={joinClasses(["h-4.5 w-0.75 rounded-full", RESULTS_HEADER_ACCENT_CLASS_NAMES.solid])} />
        <span className="grid gap-1.25">
          <span className={joinClasses(["h-0.75 w-8 rounded-full", RESULTS_HEADER_ACCENT_CLASS_NAMES.railStrong])} />
          <span className={joinClasses(["h-0.75 w-5 rounded-full", RESULTS_HEADER_ACCENT_CLASS_NAMES.dotStrong])} />
        </span>
      </span>
    );
  }

  return (
    <span className={joinClasses(["inline-flex h-7 items-center gap-1.5", className])} aria-hidden="true">
      <span className={joinClasses(["h-1.25 w-1.25 rounded-full", RESULTS_HEADER_ACCENT_CLASS_NAMES.solid])} />
      <span className={joinClasses(["h-0.75 flex-1 rounded-full", RESULTS_HEADER_ACCENT_CLASS_NAMES.rail])} />
      <span className={joinClasses(["h-1 w-1 rounded-full", RESULTS_HEADER_ACCENT_CLASS_NAMES.dot])} />
    </span>
  );
}

function ResultsHeaderTitle({ indicators = null, isNavigationPending = false, title = "" }) {
  const [isPreviewActive, setIsPreviewActive] = React.useState(false);
  const [showNavigationLoader, setShowNavigationLoader] = React.useState(false);

  React.useEffect(() => {
    if (!isNavigationPending) {
      setShowNavigationLoader(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShowNavigationLoader(true);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isNavigationPending]);

  React.useEffect(() => {
    if (!isPreviewActive) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIsPreviewActive(false);
    }, 4200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isPreviewActive]);

  const showLoadingRail = showNavigationLoader || isPreviewActive;

  return (
    <div className="flex min-w-0 items-center justify-start gap-2">
      <ResultsHeaderAccent variant="stack" className="shrink-0" />
      <div className="flex min-w-0 items-center gap-1.5">
        <h1 className="m-0 truncate text-[clamp(1.02rem,1.3vw,1.3rem)] font-semibold leading-[1.25] tracking-[-0.03em] text-slate-950">
          {title}
        </h1>
        {indicators ? <div className="flex flex-wrap items-center gap-1.25">{indicators}</div> : null}
        {showLoadingRail ? (
          <ResultsHeaderLoadingRail />
        ) : (
          <ResultsHeaderTrailAccent
            className="hidden min-[1101px]:inline-flex w-[clamp(6.5rem,11vw,11.5rem)]"
            disabled={isNavigationPending}
            isActive={isPreviewActive}
            onDotClick={() => {
              if (isNavigationPending) {
                return;
              }
              setIsPreviewActive((previousState) => !previousState);
            }}
          />
        )}
      </div>
    </div>
  );
}

function ResultsHeaderStatus({ status = null, compact = false }) {
  if (!status || typeof status !== "object") {
    return null;
  }

  const value = typeof status.value === "string" ? status.value.trim() : "";
  if (!value) {
    return null;
  }

  const label = typeof status.label === "string" && status.label.trim()
    ? status.label.trim()
    : "Validation & services";
  const isInteractive = Boolean(status.isInteractive);
  const shouldPulse = Boolean(status.shouldPulse);

  const tone = status.tone === "danger"
    ? RESULTS_HEADER_STATUS_TONE_CLASS_NAMES.danger
    : status.tone === "warning"
      ? RESULTS_HEADER_STATUS_TONE_CLASS_NAMES.warning
      : status.tone === "neutral"
        ? RESULTS_HEADER_STATUS_TONE_CLASS_NAMES.neutral
      : RESULTS_HEADER_STATUS_TONE_CLASS_NAMES.success;

  const content = (
    <>
      {!compact ? <ResultsHeaderAccent className="w-10" /> : null}
      <div className="grid gap-0 whitespace-nowrap leading-none">
        {!compact ? <span className="whitespace-nowrap text-[0.52rem] font-extrabold uppercase tracking-[0.14em] text-slate-500">{label}</span> : null}
        <span className={joinClasses(["text-[0.72rem] font-semibold leading-none", tone.text])}>{value}</span>
      </div>
      <span
        className={joinClasses([
          "h-2 w-2 rounded-none",
          tone.dot,
          shouldPulse ? "motion-safe:animate-pulse" : "",
        ])}
        aria-hidden="true"
      />
    </>
  );

  const shellClassName = joinClasses([
    "flex h-[30px] min-h-[30px] shrink-0 items-center justify-end gap-1.5 text-right",
    isInteractive
      ? "cursor-pointer rounded-[4px] border border-black/6 bg-white px-[0.7rem] py-[0.32rem] transition-[background-color,border-color,box-shadow] duration-150 hover:bg-slate-50 hover:border-black/10 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-300"
      : "",
    shouldPulse ? "ring-1 ring-red-200/70" : "",
  ]);

  if (isInteractive && typeof status.onClick === "function") {
    return (
      <button
        type="button"
        onClick={status.onClick}
        className={shellClassName}
        title={typeof status.note === "string" ? status.note : value}
        aria-label={`${label} : ${value}`}
      >
        {content}
      </button>
    );
  }

  return <div className={shellClassName}>{content}</div>;
}

function ResultsHeaderPanelToggle({
  className = "",
  collapsed = false,
  onToggle = undefined,
  side = "left",
  toggleLabel = "",
}) {
  const mirroredIconStyle = side === "right"
    ? { transform: "scaleX(-1)" }
    : undefined;

  return (
    <button
      type="button"
      onClick={onToggle}
      className={joinClasses([
        "shell-panel-toggle inline-flex h-8.5 min-w-8.5 items-center justify-center rounded-[4px] border border-line bg-white px-1.25 text-slate-600 shadow-none transition-[background-color,color] duration-150 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-300",
        className,
      ])}
      title={toggleLabel}
      aria-label={toggleLabel}
      aria-expanded={!collapsed}
    >
      {collapsed
        ? <PanelLeftOpen size={18} style={mirroredIconStyle} />
        : <PanelLeftClose size={18} style={mirroredIconStyle} />}
    </button>
  );
}

function splitOptimizationRoutePreview(value) {
  if (typeof value !== "string") {
    return [];
  }

  return value
    .split("→")
    .map((segment) => segment.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

function buildOptimizationRoutePreview(entry) {
  const labelSegments = splitOptimizationRoutePreview(entry?.label);
  const detailSegments = splitOptimizationRoutePreview(entry?.detail);
  const visibleLabelSegments = labelSegments.filter((segment) => segment !== "…");
  const visibleDetailSegments = detailSegments.filter((segment) => segment !== "…");
  const fromLabel = visibleLabelSegments[0] || (typeof entry?.label === "string" ? entry.label.trim() : "");
  const toLabel = visibleLabelSegments[visibleLabelSegments.length - 1] || fromLabel;
  const fromDetail = visibleDetailSegments[0] || "";
  const toDetail = visibleDetailSegments.length > 1
    ? visibleDetailSegments[visibleDetailSegments.length - 1]
    : (visibleDetailSegments[0] || "");

  return {
    fromDetail,
    fromLabel,
    hasCollapsedStops: labelSegments.includes("…") || detailSegments.includes("…"),
    isLoop: Boolean(fromLabel) && fromLabel.toLowerCase() === toLabel.toLowerCase(),
    toDetail,
    toLabel,
    viaDetails: visibleDetailSegments.slice(1, -1),
    viaLabels: visibleLabelSegments.slice(1, -1),
  };
}

function buildOptimizationRoutePassageLabel(preview) {
  const parts = [...preview.viaLabels];
  if (preview.hasCollapsedStops) {
    parts.push("…");
  }

  return parts.length ? parts.join(" ; ") : "Direct";
}

function buildOptimizationRoutePassageDetail(preview) {
  const parts = [...preview.viaDetails];
  if (preview.hasCollapsedStops) {
    parts.push("autres points traversés");
  }

  return parts.length ? parts.join(" ; ") : "";
}

function OptimizationStatusDenseCell({ primary, secondary = "", muted = false }) {
  return (
    <div className="min-w-0 px-2 py-1.5">
      <div className={joinClasses([
        "wrap-break-word text-[11px] leading-[1.35]",
        muted ? "font-medium text-content-muted" : "font-semibold text-content-ink",
      ])}>
        {primary}
      </div>
      {secondary ? (
        <div className="mt-0.5 wrap-break-word text-[10px] leading-[1.35] text-content-muted">
          {secondary}
        </div>
      ) : null}
    </div>
  );
}

function OptimizationStatusRouteExampleCard({ entry, showAddresses }) {
  const preview = buildOptimizationRoutePreview(entry);
  const arrivalLabel = preview.isLoop ? `${preview.toLabel} (même point)` : preview.toLabel;
  const passageLabel = buildOptimizationRoutePassageLabel(preview);
  const passageDetail = showAddresses ? buildOptimizationRoutePassageDetail(preview) : "";

  return (
    <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)_minmax(0,1.1fr)] gap-x-1 border-b border-black/6 last:border-b-0">
      <OptimizationStatusDenseCell
        primary={preview.fromLabel}
        secondary={showAddresses ? preview.fromDetail : ""}
      />
      <OptimizationStatusDenseCell
        primary={passageLabel}
        secondary={passageDetail}
        muted={passageLabel === "Direct"}
      />
      <OptimizationStatusDenseCell
        primary={arrivalLabel}
        secondary={showAddresses ? preview.toDetail : ""}
      />
    </div>
  );
}

function OptimizationStatusActivityHoverCardContent({ item }) {
  const [showAddresses, setShowAddresses] = React.useState(false);
  const label = typeof item?.label === "string" ? item.label.trim() : "";
  const value = typeof item?.value === "string" ? item.value.trim() : "";
  const description = typeof item?.description === "string" ? item.description.trim() : "";
  const example = typeof item?.example === "string" ? item.example.trim() : "";
  const examplesTitle = typeof item?.examplesTitle === "string" ? item.examplesTitle.trim() : "";
  const usesRouteLayout = item?.key === "route-cache";
  const exampleRecords = Array.isArray(item?.exampleRecords)
    ? item.exampleRecords.filter((entry) => entry && typeof entry === "object" && typeof entry.label === "string" && entry.label.trim())
    : [];
  const examples = Array.isArray(item?.examples)
    ? item.examples.filter((entry) => typeof entry === "string" && entry.trim())
    : [];
  const canToggleAddresses = exampleRecords.some((entry) => typeof entry?.detail === "string" && entry.detail.trim());

  return (
    <div className={joinClasses([
      "grid gap-2",
      usesRouteLayout ? "min-w-xl max-w-2xl" : "min-w-80 max-w-136",
    ])}>
      <div className="flex items-baseline justify-between gap-3 border-b border-black/6 pb-1.5">
        <span className="text-[10px] font-black uppercase tracking-[0.12em] text-content-faint">{label}</span>
        <strong className="text-[14px] leading-tight text-content-ink">{value}</strong>
      </div>
      {description ? <p className={joinClasses(["m-0 text-[11px] leading-[1.55] text-content-muted", usesRouteLayout ? "max-w-160" : "max-w-[24rem]"])}>{description}</p> : null}
      {exampleRecords.length ? (
        <div className="grid gap-1 border border-black/6 bg-slate-50 px-2.5 py-2">
          <div className="grid gap-1.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-black uppercase tracking-[0.12em] text-content-faint">{examplesTitle || "Exemples observés"}</span>
              <span className="text-[10px] font-semibold text-content-muted">Jusqu'à concurrence de 10</span>
            </div>
            {canToggleAddresses ? (
              <label className="inline-flex items-center justify-end gap-2 text-[10px] font-semibold text-content-muted select-none">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border border-black/12 accent-legacy-teal"
                  checked={showAddresses}
                  onChange={(event) => setShowAddresses(event.target.checked)}
                />
                <span>Afficher les adresses</span>
              </label>
            ) : null}
          </div>
          {usesRouteLayout ? (
            <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)_minmax(0,1.1fr)] gap-x-1 border-y border-black/8 bg-white px-1 py-1 text-[9px] font-black uppercase tracking-[0.12em] text-content-faint">
              <span className="px-2">Départ</span>
              <span className="px-2">Passe par</span>
              <span className="px-2">Arrivée</span>
            </div>
          ) : null}
          {!usesRouteLayout && canToggleAddresses && showAddresses ? (
            <div className="grid grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-x-2 border-y border-black/8 bg-white px-1 py-1 text-[9px] font-black uppercase tracking-[0.12em] text-content-faint">
              <span className="px-2">Point</span>
              <span className="px-2">Adresse</span>
            </div>
          ) : null}
          <div className={joinClasses(["grid overflow-y-auto pr-1", usesRouteLayout ? "max-h-96" : "max-h-72"])}>
            {exampleRecords.map((entry, index) => (
              usesRouteLayout ? (
                <OptimizationStatusRouteExampleCard
                  key={`${label || "example"}-${index}`}
                  entry={entry}
                  showAddresses={showAddresses}
                />
              ) : (
                <div
                  key={`${label || "example"}-${index}`}
                  className={joinClasses([
                    "grid border-b border-black/6 px-1 last:border-b-0",
                    canToggleAddresses && showAddresses
                      ? "grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-x-2"
                      : "grid-cols-1",
                  ])}
                >
                  <OptimizationStatusDenseCell primary={entry.label} />
                  {canToggleAddresses && showAddresses ? (
                    <OptimizationStatusDenseCell primary={entry.detail || "-"} muted />
                  ) : null}
                </div>
              )
            ))}
          </div>
        </div>
      ) : examples.length ? (
        <div className="grid gap-1 border border-black/6 bg-slate-50 px-2.5 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-black uppercase tracking-[0.12em] text-content-faint">{examplesTitle || "Exemples observés"}</span>
            <span className="text-[10px] font-semibold text-content-muted">Jusqu'à concurrence de 10</span>
          </div>
          <div className="grid max-h-72 overflow-y-auto pr-1">
            {examples.map((entry, index) => (
              <div key={`${label || "example"}-${index}`} className="border-b border-black/6 px-2 py-1.5 text-[10.5px] leading-[1.35] text-content-muted last:border-b-0">
                {entry}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {example && !examples.length ? (
        <div className="grid gap-1 border border-black/6 bg-slate-50 px-2.5 py-2">
          <span className="text-[10px] font-black uppercase tracking-[0.12em] text-content-faint">Exemple</span>
          <p className="m-0 text-[11px] leading-[1.55] text-content-muted">{example}</p>
        </div>
      ) : null}
    </div>
  );
}

const OPTIMIZATION_NOTICE_VISIBLE_ISSUE_LIMIT = 4;

function OptimizationStatusIssueList({ items }) {
  const visibleItems = items.slice(0, OPTIMIZATION_NOTICE_VISIBLE_ISSUE_LIMIT);
  const hiddenCount = Math.max(items.length - visibleItems.length, 0);

  return (
    <div className="rounded-2xl border border-rose-200/70 bg-white/85 px-3 py-3 shadow-inset-highlight">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-rose-700">
          Lignes à corriger
        </span>
        <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
          {items.length} signalée{items.length > 1 ? "s" : ""}
        </span>
      </div>

      <div className="mt-2 grid gap-2 xl:grid-cols-2">
        {visibleItems.map((item) => (
          <div
            key={item.key}
            className="rounded-xl border border-rose-200/70 bg-rose-50/70 px-3 py-2.5"
          >
            <div className="flex flex-wrap items-center gap-1.5">
              {item.orderId ? (
                <span className="inline-flex items-center rounded-full border border-rose-200 bg-white px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-rose-700">
                  Bon commande {item.orderId}
                </span>
              ) : null}
              <span className="text-[12px] font-semibold leading-5 text-slate-900">
                {item.title}
              </span>
            </div>

            {item.summary ? (
              <p className="mt-1 text-[12px] leading-5 text-slate-600">
                {item.summary}
              </p>
            ) : null}

            {Array.isArray(item.values) && item.values.length ? (
              <div className="mt-2 grid gap-1.5">
                {item.values.map((value) => (
                  <div
                    key={`${item.key}-${value.key}`}
                    className="grid grid-cols-[minmax(0,9.5rem)_minmax(0,1fr)] gap-2 text-[11px] leading-4.5"
                  >
                    <span className="font-semibold text-slate-500">
                      {value.label}
                    </span>
                    <span className="min-w-0 text-slate-800">
                      {value.value}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {hiddenCount ? (
        <p className="mt-2 text-[11px] font-medium text-slate-500">
          + {hiddenCount} autre{hiddenCount > 1 ? "s" : ""} anomalie{hiddenCount > 1 ? "s" : ""} non affichée{hiddenCount > 1 ? "s" : ""} dans ce bandeau.
        </p>
      ) : null}
    </div>
  );
}


function OptimizationStatusNotice({ className = "", notice, onDismiss, onNavigateToResults, overlayMode = "contained" }) {
  const detailItems = Array.isArray(notice?.detailItems) ? notice.detailItems : [];
  const issueItems = Array.isArray(notice?.issueItems) ? notice.issueItems : [];
  const serviceActivityItems = Array.isArray(notice?.serviceActivityItems) ? notice.serviceActivityItems : [];
  const serviceActivityNoticeKey = [
    notice?.status || "",
    notice?.finishedAt || "",
    notice?.title || "",
    notice?.message || "",
    String(issueItems.length),
    String(serviceActivityItems.length),
  ].join("|");
  const [isServiceActivityExpanded, setIsServiceActivityExpanded] = React.useState(false);

  React.useEffect(() => {
    setIsServiceActivityExpanded(false);
  }, [serviceActivityNoticeKey]);

  if (!notice) {
    return null;
  }

  const isRunning = notice.status === "running";
  const isCancelling = notice.status === "cancelling";
  const isCancelled = notice.status === "cancelled";
  const isPartial = notice.status === "partial";
  const isCompleted = notice.status === "completed";
  const startedAtLabel = formatOptimizationStatusTime(notice.startedAt);
  const Icon = isRunning || isCancelling ? LoaderCircle : isCompleted ? CheckCircle2 : AlertTriangle;
  const hasNoticeDetails = detailItems.length > 0 || issueItems.length > 0;
  const hasServiceActivity = serviceActivityItems.length > 0;
  const noticeStatusTextClassName = isRunning
    ? "text-slate-600"
    : isCancelling || isCancelled || isPartial
      ? "text-shell-status-warning-ink"
    : isCompleted
      ? "text-shell-notice-success-ink"
      : "text-shell-notice-danger-ink";
  const noticeTone = resolveOptimizationNoticeTone(notice);
  const isDangerNotice = isDangerOptimizationNotice(notice);
  const isFixedOverlay = overlayMode === "fixed";
  const noticePositionStyle = isFixedOverlay
    ? {
        left: 0,
        right: 0,
        top: 0,
        zIndex: "calc(var(--z-index-modal) + 10)",
      }
    : {
        left: 0,
        right: 0,
        top: 0,
        zIndex: "calc(var(--z-index-modal) + 10)",
      };

  return (
    <section
      className={joinClasses([
        "pointer-events-none max-h-[100dvh] overflow-y-auto",
        isFixedOverlay ? "fixed" : "fixed min-[1281px]:absolute",
        className,
      ])}
      style={noticePositionStyle}
    >
      <div className={joinClasses([
        "pointer-events-auto flex flex-col gap-3 rounded-b-[4px] border border-t-0 px-4 py-3.5 shadow-notice-shell",
          noticeTone.shell,
      ])}
        aria-busy={isRunning || isCancelling}
        aria-labelledby="optimization-status-notice-title"
        data-dispatch-alert-capture-type={isDangerNotice ? "dispatch_failure" : undefined}
        data-dispatch-alert-capture-occurrence={isDangerNotice ? serviceActivityNoticeKey : undefined}
        data-dispatch-alert-capture-title={isDangerNotice ? notice.title : undefined}
        data-dispatch-alert-capture-message={isDangerNotice ? notice.message : undefined}
        data-dispatch-alert-capture-context={isDangerNotice
          ? serializeDispatchAlertCaptureContext({
            ...(notice.captureContext && typeof notice.captureContext === "object" && !Array.isArray(notice.captureContext)
              ? notice.captureContext
              : {}),
            surface: "status_notice",
          }, {
            source: notice.source || "status_notice",
          }) || undefined
          : undefined}
      >
        <div className={joinClasses([
          "flex flex-col gap-3 lg:grid lg:items-start lg:gap-4",
          hasServiceActivity
            ? "lg:grid-cols-[minmax(0,1fr)_auto_auto]"
            : "lg:grid-cols-[minmax(0,1fr)_auto]",
        ])}>
          <div className="flex min-w-0 items-start gap-3">
            <IconContainer
              size="lg"
              className={joinClasses([
                "shadow-card-subtle",
                noticeTone.icon,
              ])}
            >
              <Icon className={joinClasses(["h-5 w-5", isRunning || isCancelling ? "motion-safe:animate-spin motion-reduce:animate-none" : ""])} />
            </IconContainer>

            <div className="grid min-w-0 gap-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
                  {notice.eyebrow}
                </span>
                {notice.statusLabel ? (
                  <>
                    <span aria-hidden="true" className="text-[10px] font-semibold text-slate-300">|</span>
                    <span className={joinClasses(["text-[11px] font-semibold", noticeStatusTextClassName])}>
                      {notice.statusLabel}
                    </span>
                    {notice.backendTotalLabel ? (
                      <span className="text-[10px] font-medium text-slate-500">
                        {notice.backendTotalLabel}
                      </span>
                    ) : null}
                  </>
                ) : null}
                {(isRunning || isCancelling) && startedAtLabel ? (
                  <>
                    <span aria-hidden="true" className="text-[10px] font-semibold text-slate-300">|</span>
                    <span className="text-[11px] font-medium text-slate-500">
                      Démarrée à {startedAtLabel}
                    </span>
                  </>
                ) : null}
              </div>

              <div id="optimization-status-notice-title" className="text-[15px] font-bold leading-6 text-slate-900">
                {notice.title}
              </div>

              <div className="flex flex-col gap-2">
                <p className="min-w-0 text-[13px] leading-6 text-slate-600">
                  {renderInlineNoBreakIdentifiers(notice.message)}
                </p>

                {hasNoticeDetails ? (
                  <div className="grid gap-2 border-t border-black/8 pt-2.5">
                    {detailItems.length ? (
                      <dl className="grid gap-1.5">
                        {detailItems.map((item) => (
                          <div key={item.key} className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:gap-2">
                            <dt className="shrink-0 text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-500">
                              {item.label}
                            </dt>
                            <dd className="m-0 min-w-0 wrap-break-word text-[13px] font-medium leading-5 text-slate-800 sm:flex-1">
                              {item.value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}

                    {issueItems.length ? (
                      <OptimizationStatusIssueList items={issueItems} />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {hasServiceActivity ? (
            <div className="flex min-w-0 flex-col gap-1 lg:max-w-none lg:justify-self-start lg:items-start lg:pr-5 xl:pr-7">
              <button
                type="button"
                onClick={() => {
                  setIsServiceActivityExpanded((currentValue) => !currentValue);
                }}
                aria-expanded={isServiceActivityExpanded}
                aria-controls="optimization-notice-service-activity-panel"
                className="inline-flex items-center gap-2 rounded-[4px] border border-black/8 bg-white px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-500 shadow-inset-highlight transition-colors duration-150 hover:bg-slate-50 hover:text-slate-700"
              >
                <ChevronRight
                  className={joinClasses([
                    "h-3.5 w-3.5 transition-transform duration-150",
                    isServiceActivityExpanded ? "rotate-90" : "",
                  ])}
                />
                <span>{notice.serviceActivityLabel || "Source / exécution"}</span>
              </button>

              {isServiceActivityExpanded ? (
                <div
                  id="optimization-notice-service-activity-panel"
                  className="flex max-w-full flex-nowrap items-center gap-1.25 overflow-x-auto pb-0.5 lg:justify-start"
                >
                  {serviceActivityItems.map((item) => (
                    <HoverCard
                      key={item.key}
                      title={item.label}
                      content={
                        item.description
                          || item.example
                          || (Array.isArray(item.examples) && item.examples.length > 0)
                          || (Array.isArray(item.exampleRecords) && item.exampleRecords.length > 0)
                          ? <OptimizationStatusActivityHoverCardContent item={item} />
                          : null
                      }
                      placement="bottom"
                      delay={120}
                      freezeAfterMs={2000}
                      maxWidth={item.key === "route-cache" ? 700 : 540}
                      disabled={
                        !item.description
                        && !item.example
                        && !(Array.isArray(item.examples) && item.examples.length > 0)
                        && !(Array.isArray(item.exampleRecords) && item.exampleRecords.length > 0)
                      }
                      accentColor={OPTIMIZATION_NOTICE_ACTIVITY_HOVER_ACCENT_COLORS[item.tone] || OPTIMIZATION_NOTICE_ACTIVITY_HOVER_ACCENT_COLORS.neutral}
                    >
                      <span
                        className={joinClasses([
                          "inline-flex shrink-0 items-center gap-1.25 whitespace-nowrap rounded-full border px-2.25 py-1 text-[10.5px] font-semibold leading-none shadow-inset-highlight",
                          OPTIMIZATION_NOTICE_ACTIVITY_TONE_CLASS_NAMES[item.tone] || OPTIMIZATION_NOTICE_ACTIVITY_TONE_CLASS_NAMES.neutral,
                        ])}
                      >
                        <span className="whitespace-nowrap font-extrabold text-slate-500/95">{item.label}</span>
                        <span className="whitespace-nowrap">{item.value}</span>
                      </span>
                    </HoverCard>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2 lg:justify-end lg:self-start">
            {(isCompleted || isCancelled) && notice.resultUrl ? (
              <ActionButton
                tone="ghost"
                size="sm"
                className="min-h-9! rounded-sm! px-3.5! text-accent! hover:text-accent-strong!"
                onClick={() => {
                  onNavigateToResults?.(notice.resultUrl || "/results");
                }}
              >
                {notice.resultActionLabel} →
              </ActionButton>
            ) : null}

            {!isRunning && !isCancelling ? (
              <button
                type="button"
                onClick={onDismiss}
                className="inline-flex h-9 w-9 items-center justify-center rounded-sm border border-black/8 bg-white text-slate-500 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-800"
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

      </div>
    </section>
  );
}

function NavItem({
  item,
  sectionLabel = "",
  sidebarCollapsed = false,
  isOpen = false,
  onNavigate = undefined,
  onToggle = undefined,
}) {
  if (!item || typeof item !== "object") {
    return null;
  }

  const itemKey = getNavItemKey(item, "nav-item");
  const subItems = getNavSubItems(item);
  const hasSubItems = subItems.length > 0;
  const hasDirectActiveState = Boolean(item.is_active || item.aria_current);
  const hasActiveDescendant = subItems.some((subItem) => hasActiveNavItem(subItem));
  const isGroupItem = hasSubItems;
  const isGroupSelected = isGroupItem && (hasActiveDescendant || hasDirectActiveState);
  const isGroupOpenOnly = isGroupItem && isOpen && !isGroupSelected;
  const isActive = !isGroupItem && hasDirectActiveState;
  const isDisabled = item.is_disabled === true;
  const disabledReason = typeof item.disabled_reason === "string" ? item.disabled_reason.trim() : "";
  const submenuId = `${itemKey}-submenu`;
  const hasDirectHref = typeof item.href === "string" && item.href.trim().length > 0;
  const disabledSurfaceClassName = "cursor-not-allowed opacity-60";
  const className = joinClasses([
    "nav-item relative flex min-h-[40px] w-full items-center gap-2.5 overflow-hidden rounded-[14px] px-3 py-2 text-slate-600 transition-colors duration-150 max-[1280px]:flex-[1_1_184px]",
    isActive ? "is-active" : "",
    isGroupItem ? "min-h-[40px] py-[7px] text-slate-600" : "",
    sidebarCollapsed ? "min-[1281px]:justify-center min-[1281px]:gap-0 min-[1281px]:px-0" : "",
  ]);
  const interactiveDataProps = getInteractiveDataProps(item);
  const selectionRail = null;
  const iconNode = ICON_MAP[item.icon || item.key] ?? <LayoutDashboard size={17} />;
  const hoverDescription = resolveNavItemHoverDescription(item);
  const hoverCardContent = hoverDescription ? (
    <NavigationHoverCardContent
      eyebrow={buildNavigationHoverEyebrow({ sectionLabel })}
      description={hoverDescription}
      detail={buildNavigationHoverDetail({ disabledReason, sectionLabel, subItemCount: subItems.length })}
    />
  ) : null;
  const labelNode = !sidebarCollapsed ? (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <span className={joinClasses([
        "flex-1 transition-all duration-200",
        hasSubItems
          ? "whitespace-normal wrap-break-word text-[0.78rem] leading-[1.15] font-semibold tracking-[0.02em] text-slate-700"
          : "truncate text-[0.9rem] font-medium text-slate-700",
      ])} data-nav-item-label="true">{item.label}</span>
    </span>
  ) : null;

  const content = hasSubItems ? (
    isDisabled ? (
      sidebarCollapsed ? (
        <div
          aria-disabled="true"
          data-nav-disabled="true"
          data-nav-group="true"
          data-nav-group-active={isGroupSelected ? "true" : undefined}
          data-nav-group-open={isOpen ? "true" : undefined}
          {...interactiveDataProps}
          className={joinClasses([className, disabledSurfaceClassName])}
        >
          <span
            className={joinClasses([
              "nav-item__icon flex items-center justify-center transition-all duration-200",
              sidebarCollapsed ? "min-[1281px]:mx-auto" : "",
            ])}
            aria-hidden="true"
          >
            {iconNode}
          </span>
        </div>
      ) : (
        <div
          data-nav-group="true"
          data-nav-group-active={isGroupSelected ? "true" : undefined}
          data-nav-group-open={isOpen ? "true" : undefined}
          aria-disabled="true"
          data-nav-disabled="true"
          {...interactiveDataProps}
          className={joinClasses([
            className,
            "pr-1.5",
            isGroupSelected
              ? "border-accent bg-accent"
              : "",
            disabledSurfaceClassName,
          ])}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2.5 rounded-sm px-1.5 py-1 text-left">
            <span className="nav-item__icon flex items-center justify-center transition-all duration-200" aria-hidden="true">
              {iconNode}
            </span>
            {labelNode}
          </span>

          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-transparent text-slate-400" aria-hidden="true">
            <ChevronRight
              size={14}
              className={joinClasses([
                "transition-transform duration-200",
                isOpen ? "rotate-90" : "",
              ])}
            />
          </span>
        </div>
      )
    ) : sidebarCollapsed ? (
      <button
        type="button"
        onClick={() => onToggle?.()}
        aria-expanded={undefined}
        aria-label={`Étendre la sidebar et afficher ${item.label}`}
        data-nav-group="true"
        data-nav-group-active={isGroupSelected ? "true" : undefined}
        data-nav-group-open={isGroupOpenOnly ? "true" : undefined}
        {...interactiveDataProps}
        className={joinClasses([
          className,
          "text-left focus-visible:outline-hidden focus-visible:ring-2",
          APP_SHELL_CLASS_NAMES.focusRing,
        ])}
      >
        <span
          className={joinClasses([
            "nav-item__icon flex items-center justify-center transition-all duration-200",
            sidebarCollapsed ? "min-[1281px]:mx-auto" : "",
          ])}
          aria-hidden="true"
        >
          {iconNode}
        </span>
      </button>
    ) : (
      <div
        data-nav-group="true"
        data-nav-group-active={isGroupSelected ? "true" : undefined}
        data-nav-group-open={isGroupOpenOnly ? "true" : undefined}
        className={joinClasses([
          className,
          "pr-1.5",
          isGroupSelected
            ? "border-accent bg-accent"
            : isGroupOpenOnly
              ? APP_SHELL_CLASS_NAMES.groupOpenSurface
              : "",
        ])}
      >
        {hasDirectHref ? (
          isInternalRouteHref(item.href) ? (
            <Link
              to={item.href}
              onClick={(event) => onNavigate?.(event, item)}
              aria-current={item.aria_current ? "page" : undefined}
              {...interactiveDataProps}
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-sm px-1.5 py-1 text-left focus-visible:outline-hidden"
            >
              <span className="nav-item__icon flex items-center justify-center transition-all duration-200" aria-hidden="true">
                {iconNode}
              </span>
              {labelNode}
            </Link>
          ) : (
            <a
              href={item.href}
              onClick={(event) => onNavigate?.(event, item)}
              aria-current={item.aria_current ? "page" : undefined}
              {...interactiveDataProps}
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-sm px-1.5 py-1 text-left focus-visible:outline-hidden"
            >
              <span className="nav-item__icon flex items-center justify-center transition-all duration-200" aria-hidden="true">
                {iconNode}
              </span>
              {labelNode}
            </a>
          )
        ) : (
          <button
            type="button"
            onClick={() => onToggle?.()}
            {...interactiveDataProps}
            className="flex min-w-0 flex-1 items-center gap-2.5 rounded-sm px-1.5 py-1 text-left focus-visible:outline-hidden"
          >
            <span className="nav-item__icon flex items-center justify-center transition-all duration-200" aria-hidden="true">
              {iconNode}
            </span>
            {labelNode}
          </button>
        )}

        <button
          type="button"
          onClick={() => onToggle?.()}
          aria-expanded={isOpen}
          aria-controls={submenuId}
          aria-label={isOpen ? `Réduire ${item.label}` : `Déployer ${item.label}`}
          className={joinClasses([
            "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-transparent text-slate-500 transition-colors duration-150 focus-visible:outline-hidden focus-visible:ring-2",
            APP_SHELL_CLASS_NAMES.focusRing,
            isOpen || isGroupSelected
              ? APP_SHELL_CLASS_NAMES.groupToggleActive
              : "hover:bg-slate-100 hover:text-slate-700",
          ])}
        >
          <ChevronRight
            size={14}
            className={joinClasses([
              "transition-transform duration-200",
              isOpen ? "rotate-90" : "",
            ])}
            aria-hidden="true"
          />
        </button>
      </div>
    )
  ) : isDisabled ? (
    <div
      className={joinClasses([className, disabledSurfaceClassName])}
      aria-disabled="true"
      data-nav-disabled="true"
      {...interactiveDataProps}
    >
      {selectionRail}
      <span
        className={joinClasses([
          "nav-item__icon flex items-center justify-center transition-all duration-200",
          sidebarCollapsed ? "min-[1281px]:mx-auto" : "",
        ])}
        aria-hidden="true"
      >
        {iconNode}
      </span>
      {labelNode}
    </div>
  ) : item.detail_view_link ? (
    isInternalRouteHref(item.href) ? (
      <Link
        className={className}
        to={item.href}
        onClick={(event) => onNavigate?.(event, item)}
        aria-current={item.aria_current ? "page" : undefined}
        {...interactiveDataProps}
      >
        {selectionRail}
        <span
          className={joinClasses([
            "nav-item__icon flex items-center justify-center transition-all duration-200",
            sidebarCollapsed ? "min-[1281px]:mx-auto" : "",
          ])}
          aria-hidden="true"
        >
          {iconNode}
        </span>
        {labelNode}
      </Link>
    ) : (
      <a
        className={className}
        href={item.href}
        onClick={(event) => onNavigate?.(event, item)}
        aria-current={item.aria_current ? "page" : undefined}
        {...interactiveDataProps}
      >
        {selectionRail}
        <span
          className={joinClasses([
            "nav-item__icon flex items-center justify-center transition-all duration-200",
            sidebarCollapsed ? "min-[1281px]:mx-auto" : "",
          ])}
          aria-hidden="true"
        >
          {iconNode}
        </span>
        {labelNode}
      </a>
    )
  ) : (
    <NavLink
      className={className}
      to={item.href || "/"}
      onClick={(event) => onNavigate?.(event, item)}
      end
      {...interactiveDataProps}
    >
      {selectionRail}
      <span
        className={joinClasses([
          "nav-item__icon flex items-center justify-center transition-all duration-200",
          sidebarCollapsed ? "min-[1281px]:mx-auto" : "",
        ])}
        aria-hidden="true"
      >
        {iconNode}
      </span>
      {labelNode}
    </NavLink>
  );

  const wrappedContent = hoverCardContent ? (
    <HoverCard title={item.label} content={hoverCardContent} placement="right" maxWidth={340} hoverOnly delay={MAIN_NAV_HOVERCARD_DELAY_MS}>
      {content}
    </HoverCard>
  ) : content;

  return (
    <div className="flex w-full flex-col gap-0.5">
      {wrappedContent}

      {hasSubItems && !sidebarCollapsed ? (
        <div
          id={submenuId}
          className={joinClasses([
            "mt-1.5 ml-3 flex flex-col gap-1 overflow-hidden border-l border-black/60 pl-3 transition-all duration-200 ease-in-out",
            isOpen ? "max-h-75 opacity-100" : "max-h-0 opacity-0",
          ])}
          data-nav-submenu="true"
        >
          {subItems.map((subItem, index) => (
            <SubNavItem
              key={getNavItemKey(subItem, `${itemKey}-sub-item-${index}`)}
              item={subItem}
              onNavigate={onNavigate}
              parentLabel={item.label}
              sectionLabel={sectionLabel}
            />
          ))}
        </div>
      ) : null}

      {hasSubItems && sidebarCollapsed ? (
        <div className="mt-0.5 flex flex-col gap-1">
          {subItems
            .filter((subItem) => Boolean(subItem.is_active || subItem.aria_current))
            .map((subItem, index) => {
              const subItemDescription = resolveNavItemHoverDescription(subItem);
              const subItemHoverCardContent = subItemDescription ? (
                <NavigationHoverCardContent
                  eyebrow={buildNavigationHoverEyebrow({ parentLabel: item.label, sectionLabel })}
                  description={subItemDescription}
                  detail={buildNavigationHoverDetail({ parentLabel: item.label, sectionLabel })}
                />
              ) : null;
              const subItemNode = isInternalRouteHref(subItem.href) ? (
                <Link
                  to={subItem.href}
                  onClick={(event) => onNavigate?.(event, subItem)}
                  aria-current={subItem.aria_current ? "page" : undefined}
                  className="shell-subnav-item is-active mx-auto flex h-5 w-8 items-center justify-center rounded-[4px]"
                >
                  <span className="h-1 w-1 rounded-none bg-current" />
                </Link>
              ) : (
                <a
                  href={subItem.href}
                  onClick={(event) => onNavigate?.(event, subItem)}
                  aria-current={subItem.aria_current ? "page" : undefined}
                  className="shell-subnav-item is-active mx-auto flex h-5 w-8 items-center justify-center rounded-[4px]"
                >
                  <span className="h-1 w-1 rounded-none bg-current" />
                </a>
              );

              return subItemHoverCardContent ? (
                <HoverCard
                  key={getNavItemKey(subItem, `${itemKey}-sub-active-${index}`)}
                  title={subItem.label}
                  content={subItemHoverCardContent}
                  placement="right"
                  maxWidth={340}
                  hoverOnly delay={MAIN_NAV_HOVERCARD_DELAY_MS}
                >
                  {subItemNode}
                </HoverCard>
              ) : (
                <React.Fragment key={getNavItemKey(subItem, `${itemKey}-sub-active-${index}`)}>
                  {subItemNode}
                </React.Fragment>
              );
            })}
        </div>
      ) : null}
    </div>
  );
}

export default function AppShell({
  children = null,
  isSigningOut = false,
  onManualSignOut = undefined,
}) {
  const { access, isReady: isAccessReady } = useAccess();
  const isRestrictedBillingWorkspace = isBillingWorkspaceOnly(access);
  const canShowTodayStalePortalAlert = isAccessReady
    && !isRestrictedBillingWorkspace
    && canPublishDriverPortal(access);
  const todayStalePortalAlert = useTodayStalePortalRoutesAlert({
    enabled: canShowTodayStalePortalAlert,
  });
  useDiagnosticsAutoSnapshots({ access });
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const navigateToResults = React.useCallback((url = "/results") => {
    navigate(enrichResultsUrlWithStoredDispatchContext(url));
  }, [navigate]);
  const pathname = location.pathname;
  const isResultsPage = pathname === "/results" || pathname === "/results/latest";
  const isReportsPage = pathname === "/reports";
  const isCapturesHubPage = pathname === "/captures";
  const isDispatchAlertCapturesPage = pathname === "/system-config/dispatch-alert-captures";
  const isReassignmentDiagnosticsPage = pathname === "/system-config/reassignment-diagnostics";
  const isSystemConfigSubpage = isCapturesHubPage || isDispatchAlertCapturesPage || isReassignmentDiagnosticsPage;
  const canViewCaptureBadges = isAccessReady && canManageSystemDiagnostics(access);
  const dispatchAlertCaptureState = useDispatchAlertCapture({
    pathname,
    search: location.search,
  });
  const isAlertCaptureConfigActive = useDispatchAlertCaptureConfigActive({
    enabled: isSystemConfigSubpage && canViewCaptureBadges,
  });
  const isReassignmentCaptureHistoryActive = useReassignmentDiagnosticsHistoryActive({
    enabled: (isResultsPage || isSystemConfigSubpage) && canViewCaptureBadges,
  });
  const isAuthenticatedHomePage = pathname === "/accueil";
  const isDashboardPage = pathname === "/dashboard";
  const isSystemConfigPage = pathname === "/system-config" || isSystemConfigSubpage;
  const shellData = useShellData(
    isSystemConfigSubpage ? "/system-config" : pathname === "/admin/local-tools" ? "/admin" : pathname,
    {
      enabled: isAccessReady
        && !isAuthenticatedHomePage
        && (!isRestrictedBillingWorkspace || ["/billing", "/consolidation"].includes(pathname)),
    },
  );
  const derivedPageKind = isResultsPage ? "results"
    : isReportsPage ? "reports"
    : isAuthenticatedHomePage ? "home"
    : isDashboardPage ? "dashboard"
    : pathname === "/admin" || pathname === "/admin/local-tools" ? "admin"
    : pathname === "/admin/automations" ? "automations"
    : pathname === "/billing" ? "billing"
    : pathname === "/driver-accounts" ? "driver_accounts"
    : pathname === "/orders" ? "orders"
    : pathname === "/updates" ? "updates"
    : pathname === "/dev/animation-chargement" ? "loading_preview"
    : pathname === "/data-manager" ? "data_manager"
    : isSystemConfigPage ? "system_config"
    : "dashboard";
  const pageKind = isAuthenticatedHomePage
    ? "home"
    : ["/billing", "/consolidation"].includes(pathname)
    ? "billing"
    : pathname === "/admin/automations"
    ? "automations"
    : isSystemConfigPage
      ? "system_config"
      : typeof shellData?.page_kind === "string" && shellData.page_kind.trim()
        ? shellData.page_kind.trim()
        : derivedPageKind;
  const usesResultsShellPalette = RESULTS_THEMED_PAGE_KINDS.has(pageKind);

  React.useEffect(() => {
    if (typeof window === "undefined" || isRestrictedBillingWorkspace) {
      return undefined;
    }

    const preloadNavigationTargets = () => {
      void import("./AdminPage.jsx").catch(() => {});
      void import("./BillingPage.jsx").catch(() => {});
      void import("./DataManagerPage.jsx").catch(() => {});
      void import("./SystemConfigPage.jsx").catch(() => {});
      void import("./OrdersPage.jsx").catch(() => {});
    };

    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(preloadNavigationTargets, { timeout: 1200 });
      return () => {
        window.cancelIdleCallback?.(idleId);
      };
    }

    const timeoutId = window.setTimeout(preloadNavigationTargets, 180);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isRestrictedBillingWorkspace]);

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    document.body.classList.add("page-shell");

    if (usesResultsShellPalette) {
      document.body.style.background = "var(--results-shell-outer)";
      document.documentElement.style.background = "var(--results-shell-outer)";
    } else {
      document.body.style.background = "";
      document.documentElement.style.background = "";
    }

    if (isResultsPage) {
      document.body.classList.add("results-page");
    } else {
      document.body.classList.remove("results-page");
    }

    return () => {
      document.body.classList.remove("page-shell");
      document.body.classList.remove("results-page");
      document.body.style.background = "";
      document.documentElement.style.background = "";
    };
  }, [isResultsPage, usesResultsShellPalette]);

  const [brand, setBrand] = React.useState(readStoredShellBrand);
  React.useEffect(() => {
    if (shellData && shellData.brand && typeof shellData.brand === "object") {
      setBrand(shellData.brand);
      storeShellBrand(shellData.brand);
    }
  }, [shellData]);
  const header = !isAuthenticatedHomePage && shellData && typeof shellData.header === "object" ? shellData.header : {};
  const { visibleAnnouncedUpdates } = useProductUpdates();
  const hasVisibleProductUpdate = !isRestrictedBillingWorkspace && visibleAnnouncedUpdates.length > 0;
  const productUpdateOverlayRef = React.useRef(null);
  const [productUpdateOverlayHeight, setProductUpdateOverlayHeight] = React.useState(0);
  const {
    dismissOptimizationNotice,
    jobState: optimizationJobState,
    requestOptimizationCancel,
  } = useOptimizationJobTracker();
  const {
    activeStatusNotice: trackedStatusNotice,
    dismissStatusNotice,
  } = useShellStatus({
    optimizationPollingEnabled: !isRestrictedBillingWorkspace,
  });
  const shouldRenderOptimizationProgressCard = !isRestrictedBillingWorkspace
    && isFleetOptimizationProgressCardVisible(optimizationJobState);
  const shouldSuppressOptimizationJobBanner = shouldRenderOptimizationProgressCard
    && trackedStatusNotice?.source === "optimization"
    && trackedStatusNotice?.status === optimizationJobState.status;
  const activeStatusNotice = isRestrictedBillingWorkspace
    || shouldSuppressOptimizationJobBanner
    || (isAuthenticatedHomePage && trackedStatusNotice?.source !== "optimization")
    ? null
    : trackedStatusNotice;
  const [isStatusNoticeMinimized, setIsStatusNoticeMinimized] = React.useState(false);
  React.useEffect(() => {
    if (!activeStatusNotice) {
      return undefined;
    }

    const status = activeStatusNotice.status;
    const isTerminal = status !== "running" && status !== "cancelling";
    if (!isTerminal || isDangerOptimizationNotice(activeStatusNotice)) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setIsStatusNoticeMinimized(true);
    }, 8000);
    return () => {
      window.clearTimeout(timerId);
    };
  }, [activeStatusNotice]);
  React.useEffect(() => {
    setIsStatusNoticeMinimized(false);
  }, [activeStatusNotice]);

  const fleetFullscreenOverlaySnapshot = React.useSyncExternalStore(
    subscribeFleetFullscreenOverlay,
    getFleetFullscreenOverlaySnapshot,
    getFleetFullscreenOverlaySnapshot,
  );
  const isFleetFullscreenOverlayActive = fleetFullscreenOverlaySnapshot.isActive === true;
  const [bootstrappedResultsPanelData] = React.useState(() => {
    if (typeof document === "undefined") {
      return null;
    }

    const scriptElement = document.getElementById("results-data");
    if (!(scriptElement instanceof HTMLScriptElement)) {
      return null;
    }

    try {
      return buildResultsRightPanelData(JSON.parse(scriptElement.textContent || "null"));
    } catch {
      return null;
    }
  });
  const subscribedServiceValidation = React.useSyncExternalStore(
    subscribeResultsRightPanel,
    getResultsServiceValidationSnapshot,
    getResultsServiceValidationSnapshot,
  );
  const fallbackNavActiveKey = isResultsPage
    ? getResultsDefaultNavKey(pathname)
    : getPathnameNavKey(pathname);
  const fallbackNavSections = React.useMemo(() => buildFallbackNavSections({
    activeNavKey: fallbackNavActiveKey,
  }), [fallbackNavActiveKey]);
  const baseNavSections = Array.isArray(shellData?.nav_sections) && shellData.nav_sections.length > 0
    ? shellData.nav_sections
    : fallbackNavSections;
  const navSections = React.useMemo(() => {
    if (isRestrictedBillingWorkspace) {
      const restrictedItems = [];
      if (canAccessConsolidationPage(access)) {
        restrictedItems.push({
          aria_current: pathname === "/consolidation",
          href: "/consolidation",
          icon: "consolidation",
          is_active: pathname === "/consolidation",
          key: "consolidation",
          label: "Consolidation",
        });
      }
      if (canAccessBillingPage(access)) {
        restrictedItems.push({
          aria_current: pathname === "/billing",
          href: "/billing",
          icon: "billing",
          is_active: pathname === "/billing",
          key: "billing",
          label: "Facturation",
        });
      }
      const restrictedSections = restrictedItems.length > 0 ? [{ label: "Facturation", items: restrictedItems }] : [];
      if (canAccessGeotabPage(access)) {
        restrictedSections.push({ label: "Opérations", items: [{
          aria_current: pathname === "/geotab",
          href: "/geotab",
          icon: "geotab",
          is_active: pathname === "/geotab",
          key: "geotab",
          label: "Geotab",
        }] });
      }
      return restrictedSections;
    }

    return injectLocalToolsNavItem(injectBillingNavSection(
      injectAutomationsNavItem(
        injectAdminNavSection(
          injectPortalNavItem(
            injectDriverAccountsNavItem(
              injectProductUpdatesNavItem(filterNavSectionsByAccess(baseNavSections, access)),
              { hasAccess: canManageDriverAccounts(access) },
            ),
            { hasAccess: canAccessDriverPortal(access) },
          ),
          { hasAccess: canAccessAdminPage(access) },
        ),
        { hasAccess: canManageAutomations(access) },
      ),
      {
        billingAccess: canAccessBillingPage(access),
        consolidationAccess: canAccessConsolidationPage(access),
        pathname,
      },
    ), { hasAccess: canAccessLocalToolsPage(access) });
  }, [access, baseNavSections, isRestrictedBillingWorkspace, pathname]);
  const hasAdminNavSection = navSections.some((section) => (
    typeof section?.label === "string" && section.label.trim().toLowerCase() === "admin"
  ));
  const isCompactShell = useMediaQuery(COMPACT_SHELL_QUERY);
  const [mobileNavigationOpen, setMobileNavigationOpen] = React.useState(false);
  React.useEffect(() => {
    setMobileNavigationOpen(false);
  }, [isCompactShell, location.key, location.pathname, location.search, location.hash]);
  const {
    activeNavKey,
    handleNavItemNavigate,
    openNavItemKey,
    resolvedNavSections,
    setSidebarCollapsed,
    sidebarCollapsed,
    toggleNavItem,
  } = useShellNavigation({
    isResultsPage,
    location,
    navigate,
    navSections,
    compact: isCompactShell,
  });
  const [rightPanelCollapsed, setRightPanelCollapsed] = React.useState(readStoredRightPanelCollapsed);
  const storedRightPanel = useUserUiPreferenceStorageValue("local", RIGHT_PANEL_COLLAPSED_STORAGE_KEY);
  React.useEffect(() => { setRightPanelCollapsed(readStoredRightPanelCollapsed()); }, [storedRightPanel]);
  const hasRightPanel = isResultsPage && !isRestrictedBillingWorkspace;
  const pageSurfaceWidthClassName = resolvePageSurfaceWidthClassName(pageKind);
  const headerFallback = getShellHeaderFallback(pathname, navSections, activeNavKey);
  const headerActions = Array.isArray(header.actions) ? header.actions : [];
  const headerIndicators = Array.isArray(header.indicators) ? header.indicators : [];
  const shellHeaderStatus = header && typeof header.status === "object" ? header.status : null;
  const isPageNavigationPending = navigation.state === "loading" && Boolean(navigation.location);
  const navigationStatusLabel = navigation.location?.pathname === pathname
    ? "Actualisation de la vue…"
    : "Ouverture de la page…";
  const headerActionNodes = [
    <AskEpiButton key="ask-epi" pageTitle={headerFallback.title} />,
    <NotificationCenterButton key="notification-center" />,
    ...headerActions.map((action) => (
      <HeaderAction key={action.key} action={action} />
    )),
    ...(typeof onManualSignOut === "function"
      ? [(
        <button
          key="manual-sign-out"
          type="button"
          onClick={() => {
            void onManualSignOut();
          }}
          disabled={isSigningOut}
          className={joinClasses([
            HEADER_ACTION_BASE_CLASS_NAME,
            HEADER_ACTION_TONE_CLASS_NAMES.neutral,
            isResultsPage ? "min-[1281px]:hidden!" : "",
            isSigningOut ? "cursor-wait opacity-80" : "",
          ])}
          aria-label="Déconnexion"
        >
          {isSigningOut ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
          {isSigningOut ? "Déconnexion..." : "Déconnexion"}
        </button>
      )]
      : []),
  ];
  const headerIndicatorNodes = headerIndicators
    .map((indicator, index) => {
      const key = typeof indicator?.key === "string" && indicator.key.trim()
        ? indicator.key.trim()
        : `header-indicator-${index}`;
      return <HeaderIndicator key={key} indicator={indicator} />;
    })
    .filter(Boolean);
  const normalizedShellHeaderStatus = shellHeaderStatus && typeof shellHeaderStatus.value === "string" && shellHeaderStatus.value.trim()
    ? {
        errorFilter: typeof shellHeaderStatus.error_filter === "string" ? shellHeaderStatus.error_filter : "",
        errorSource: typeof shellHeaderStatus.error_source === "string" ? shellHeaderStatus.error_source : "",
        isInteractive: Boolean(shellHeaderStatus.is_interactive),
        label: typeof shellHeaderStatus.label === "string" && shellHeaderStatus.label.trim()
          ? shellHeaderStatus.label.trim()
          : "Validation & services",
        note: typeof shellHeaderStatus.note === "string" ? shellHeaderStatus.note : "",
        shouldPulse: Boolean(shellHeaderStatus.should_pulse),
        tone: shellHeaderStatus.tone === "danger"
          || shellHeaderStatus.tone === "warning"
          || shellHeaderStatus.tone === "neutral"
          ? shellHeaderStatus.tone
          : "success",
        value: shellHeaderStatus.value.trim(),
      }
    : null;
  const resolvedHeaderEyebrow = usesResultsShellPalette ? "" : (header.eyebrow || headerFallback.eyebrow);
	const resolvedHeaderTitle = pathname === "/consolidation"
		? "Consolidation"
		: pageKind === "automations"
		? "Tâches planifiées"
		: isCapturesHubPage
		? (headerFallback.title || header.title || shellData?.page_title || "TransIA")
		: (header.title || headerFallback.title || shellData?.page_title || "TransIA");
  const resolvedHeaderSubtitle = usesResultsShellPalette ? "" : (header.subtitle || headerFallback.subtitle);
  const sidebarWidth = sidebarCollapsed ? "72px" : "216px";
  const sidebarToggleLabel = sidebarCollapsed ? "Étendre la sidebar" : "Réduire la sidebar";
  const rightPanelWidth = hasRightPanel ? (rightPanelCollapsed ? "46px" : "240px") : "0px";
  const rightPanelToggleLabel = rightPanelCollapsed ? "Étendre le panneau droit" : "Réduire le panneau droit";
  const resultsServiceValidation = subscribedServiceValidation || bootstrappedResultsPanelData?.serviceValidation || null;
  const resultsHeaderLead = usesResultsShellPalette ? (
    <ResultsHeaderLead
      isNavigationPending={isPageNavigationPending}
      sidebarCollapsed={sidebarCollapsed}
    />
  ) : null;
  const resolvedHeaderStatus = isRestrictedBillingWorkspace || pathname === "/consolidation"
    ? null
    : isResultsPage
    ? resultsServiceValidation || normalizedShellHeaderStatus || null
    : normalizedShellHeaderStatus;
  React.useEffect(() => {
    if (!hasVisibleProductUpdate) {
      setProductUpdateOverlayHeight(0);
      return undefined;
    }

    const overlayElement = productUpdateOverlayRef.current;
    if (!(overlayElement instanceof HTMLDivElement)) {
      return undefined;
    }

    const updateOverlayHeight = () => {
      const nextOverlayHeight = Math.ceil(overlayElement.getBoundingClientRect().height);
      setProductUpdateOverlayHeight((previousHeight) => (
        previousHeight === nextOverlayHeight ? previousHeight : nextOverlayHeight
      ));
    };

    updateOverlayHeight();

    if (typeof ResizeObserver !== "function") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateOverlayHeight();
    });
    resizeObserver.observe(overlayElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [hasVisibleProductUpdate, visibleAnnouncedUpdates.length]);
  const handleHeaderStatusClick = React.useCallback(() => {
    if (!resolvedHeaderStatus?.isInteractive) {
      return;
    }

    dispatchResultsShellNavigation({
      errorId: "",
      errorsOpenAll: true,
      filter: typeof resolvedHeaderStatus.errorFilter === "string" ? resolvedHeaderStatus.errorFilter : "",
      historyMode: "replace",
      scroll: true,
      source: typeof resolvedHeaderStatus.errorSource === "string" && resolvedHeaderStatus.errorSource
        ? resolvedHeaderStatus.errorSource
        : "results_overview",
      syncLocation: true,
      viewName: resolvedHeaderStatus.errorViewName === "warnings" ? "warnings" : "errors",
    });
    if (!isResultsPage) {
      navigateToResults();
    }
  }, [isResultsPage, navigateToResults, resolvedHeaderStatus]);
  const renderedHeaderStatus = resolvedHeaderStatus?.isInteractive
    ? {
        ...resolvedHeaderStatus,
        onClick: handleHeaderStatusClick,
      }
    : resolvedHeaderStatus;
  const shouldRenderTopbar = Boolean(
    resolvedHeaderEyebrow
      || resolvedHeaderTitle
      || resolvedHeaderSubtitle
      || headerIndicatorNodes.length > 0
      || renderedHeaderStatus
      || headerActionNodes.length > 0,
  );
  const shouldRenderColorIntensityControl = usesResultsShellPalette && !isResultsPage;
  const shouldShowDispatchAlertCaptureBadge = canManageSystemDiagnostics(access) && (
    isResultsPage
      ? dispatchAlertCaptureState.isActive
      : isSystemConfigSubpage && isAlertCaptureConfigActive
  );
  const shouldShowReassignmentCaptureBadge = canManageSystemDiagnostics(access)
    && (isResultsPage || isSystemConfigSubpage)
    && isReassignmentCaptureHistoryActive;
  const shouldRenderTopbarRightRail = usesResultsShellPalette
    ? Boolean(
      renderedHeaderStatus
      || headerActionNodes.length > 0
      || shouldRenderColorIntensityControl
      || shouldShowDispatchAlertCaptureBadge
      || shouldShowReassignmentCaptureBadge,
    )
    : hasRightPanel && Boolean(
      renderedHeaderStatus
      || headerActionNodes.length > 0,
    );
  const shouldReserveTopbarRightColumn = hasRightPanel
    || (usesResultsShellPalette && shouldRenderTopbarRightRail);
  const shellFrameClassName = resolveAppShellFrameClassName();
  const shellDesktopColumnsClassName = hasRightPanel
      ? "min-[1281px]:grid-cols-[var(--shell-sidebar-width)_minmax(0,1fr)_var(--shell-right-panel-width)]"
    : "min-[1281px]:grid-cols-[var(--shell-sidebar-width)_minmax(0,1fr)]";
  const topbarDesktopColumnsClassName = shouldReserveTopbarRightColumn
      ? "min-[1281px]:grid-cols-[216px_minmax(0,1fr)_max-content]"
    : "min-[1281px]:grid-cols-[216px_minmax(0,1fr)]";
  const shellLayoutClassName = joinClasses([
    shellFrameClassName,
    "relative isolate grid min-h-screen gap-0 overflow-hidden rounded-none border-2 border-border-results-frame bg-transparent shadow-[0_0_0_1px_var(--color-border-results-frame)] min-[1281px]:h-[100dvh] max-[1280px]:grid-cols-1 max-[1280px]:rounded-none",
    shouldRenderTopbar ? "grid-rows-[auto_minmax(0,1fr)] items-stretch" : "items-start",
    shellDesktopColumnsClassName,
    hasRightPanel ? "min-[1281px]:border-r-0" : "",
  ]);
  const sidebarSectionClassName = isResultsPage ? "flex flex-col gap-3.5" : "flex flex-col gap-4";
  const sidebarNavClassName = joinClasses([
    "flex flex-col gap-2 rounded-[22px] border px-2.25 py-2.5 shadow-shell-inset max-[1280px]:flex-row max-[1280px]:flex-wrap max-[1280px]:gap-2 max-[1280px]:rounded-[18px] max-[1280px]:px-2 max-[1280px]:py-2.5 max-[720px]:gap-2",
    "min-[1281px]:min-h-0 min-[1281px]:flex-1 min-[1281px]:overflow-y-auto",
    usesResultsShellPalette
      ? "border-shell-sidebar-nav-results-border bg-surface-results-nav"
      : "border-shell-sidebar-nav-default-border bg-gray-200",
  ]);
  const sidebarSignOutButtonClassName = joinClasses([
    "shell-sign-out inline-flex min-h-[40px] w-full items-center gap-2.5 rounded-[4px] border border-slate-300 bg-white px-3 py-2 text-left text-slate-600 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-300",
    sidebarCollapsed ? "min-[1281px]:justify-center min-[1281px]:gap-0 min-[1281px]:px-0" : "",
  ]);
  const shellMainClassName = joinClasses([
    "relative min-w-0 flex min-h-0 flex-col max-[1280px]:flex-[1_0_auto]",
    usesResultsShellPalette
      ? "bg-surface-results"
      : "bg-surface-shell-main-default",
  ]);
  const topbarClassName = joinClasses([
    "col-[1/-1] grid overflow-visible rounded-t-none rounded-b-none text-text max-[1280px]:rounded-t-none",
    usesResultsShellPalette
      ? "border-0 bg-surface-results-topbar shadow-none"
      : "border border-shell-topbar-default-border bg-surface-topbar-default shadow-topbar-default",
    usesResultsShellPalette ? topbarDesktopColumnsClassName : shellDesktopColumnsClassName,
  ]);
  const topbarMainCellClassName = joinClasses([
    usesResultsShellPalette ? "min-w-0 px-3.5 py-0.75 max-[720px]:px-3.5 max-[720px]:py-0.75" : "min-w-0 px-5 py-4 max-[720px]:px-4 max-[720px]:py-3.5",
    "min-[1281px]:col-start-2",
  ]);
  const topbarActionsClassName = "relative z-1 self-start flex flex-wrap items-center gap-2 max-[720px]:w-full";
  const topbarActionRailClassName = "px-5 py-4 max-[1280px]:px-4 max-[1280px]:pt-0 max-[1280px]:pb-3.5 min-[1281px]:flex min-[1281px]:items-start min-[1281px]:justify-end";
  const resultsTopbarRightRailClassName = "hidden min-w-0 min-[1281px]:col-start-3 min-[1281px]:flex min-[1281px]:items-center min-[1281px]:justify-end px-3 py-0.75";
  const standardPageFrameClassName = joinClasses([
    "mx-auto w-full px-5 max-[720px]:px-4",
    pageSurfaceWidthClassName,
  ]);
  const optimizationNoticeFrameClassName = joinClasses([
    "z-40 w-full",
  ]);
  const optimizationNoticeOverlayFrameClassName = joinClasses([
    standardPageFrameClassName,
    "relative z-40 pt-3 max-[720px]:pt-2",
  ]);
  const productUpdateOverlayClassName = joinClasses([
    "pointer-events-none absolute z-[70] top-3 left-3 right-3 max-h-[calc(100vh-1rem)] overflow-y-auto max-[720px]:top-2 max-[720px]:left-2 max-[720px]:right-2",
    "min-[1281px]:left-[calc(var(--shell-sidebar-width)+1rem)]",
    hasRightPanel ? "min-[1281px]:right-[calc(var(--shell-right-panel-width)+1rem)]" : "min-[1281px]:right-4",
  ]);
  const todayStalePortalOverlayClassName = joinClasses([
    "pointer-events-none absolute z-[75] right-3 w-[min(28rem,calc(100vw-1.5rem))] max-[720px]:right-2",
    hasRightPanel ? "min-[1281px]:right-[calc(var(--shell-right-panel-width)+1rem)]" : "min-[1281px]:right-4",
  ]);
  const contentClassName = joinClasses([
    isResultsPage
      ? "min-w-0 flex flex-1 min-h-0 flex-col gap-0 pl-1.5 pt-[calc(var(--product-update-overlay-height)+0.5rem)] min-[1281px]:overflow-y-auto max-[720px]:pt-[calc(var(--product-update-overlay-height)+0.4rem)]"
      : usesResultsShellPalette
        ? "min-w-0 flex flex-1 min-h-0 flex-col gap-0 pt-[calc(var(--product-update-overlay-height)+0.75rem)] min-[1281px]:overflow-y-auto max-[720px]:pt-[calc(var(--product-update-overlay-height)+0.625rem)]"
        : "min-w-0 flex flex-1 min-h-0 flex-col gap-2 pt-[var(--product-update-overlay-height)] min-[1281px]:overflow-y-auto",
    // Consolidation et Facturation : même marge que Geotab des deux côtés (10,5 px avec celle de la page).
    pageKind === "billing"
      ? "min-[1281px]:px-1"
      : ["reports", "dashboard", "updates", "automations"].includes(pageKind)
        ? "min-[1281px]:[scrollbar-gutter:stable]"
        : "",
    // Pages sans RightPanel : petite gouttière (le vrai rail droit vient du panneau quand il est réservé).
    !hasRightPanel
      ? (["updates", "automations"].includes(pageKind)
        ? (rightPanelCollapsed ? "min-[1281px]:pr-[46px]" : "min-[1281px]:pr-[188px]")
        // Geotab, le tableau de bord, la Consolidation et la Facturation occupent toute la largeur, centrés.
        : ["geotab", "dashboard", "billing"].includes(pageKind) ? "" : "min-[1281px]:pr-16 min-[1600px]:pr-20")
      : "",
  ]);
  const sidebarClassName = joinClasses([
    "sidebar sticky top-0 z-20 flex min-h-0 flex-col justify-start gap-3 overflow-hidden rounded-[30px] border text-slate-700 transition-all duration-250 ease-in-out min-[1281px]:rounded-t-none min-[1281px]:rounded-r-none max-[1280px]:h-auto max-[1280px]:min-h-0 max-[1280px]:w-full max-[1280px]:top-auto max-[720px]:rounded-[22px]",
    "h-full",
    usesResultsShellPalette
      ? "min-[1281px]:border-t-transparent min-[1281px]:border-l-transparent min-[1281px]:border-b-transparent border-border-results-shell-strong bg-surface-results-sidebar shadow-sidebar-results"
      : "border-shell-sidebar-default-border bg-surface-sidebar-default shadow-sidebar-default",
    sidebarCollapsed ? "min-[1281px]:w-[72px]" : "min-[1281px]:w-[216px]",
  ]);

  // Children are now direct React elements (AppRouter), no longer a render function.
  const resolvedChildren = children;
  const noticeElement = activeStatusNotice && (!isStatusNoticeMinimized || isDangerOptimizationNotice(activeStatusNotice)) ? (
    <OptimizationStatusNotice
      className={isFleetFullscreenOverlayActive ? optimizationNoticeOverlayFrameClassName : optimizationNoticeFrameClassName}
      notice={activeStatusNotice}
      onDismiss={dismissStatusNotice}
      onNavigateToResults={navigateToResults}
      overlayMode={isFleetFullscreenOverlayActive ? "fixed" : "contained"}
    />
  ) : null;
  const fullscreenNoticeMountNode = isFleetFullscreenOverlayActive && getReactPortalRoot();
  const minimizedNoticeStripStyle = {
    height: "5.5rem",
    top: shouldRenderTopbar
      ? "calc(var(--product-update-overlay-height) + 3.25rem)"
      : "calc(var(--product-update-overlay-height) + 0.75rem)",
  };
  const todayStalePortalOverlayStyle = {
    top: shouldRenderTopbar
      ? "calc(var(--product-update-overlay-height) + 3.5rem)"
      : "calc(var(--product-update-overlay-height) + 0.75rem)",
  };
  const floatingOptimizationProgressStyle = {
    top: shouldRenderTopbar
      ? "calc(var(--product-update-overlay-height) + 3rem)"
      : "calc(var(--product-update-overlay-height) + 0.75rem)",
  };
  const minimizedNoticeStripElement = isStatusNoticeMinimized && activeStatusNotice && !isDangerOptimizationNotice(activeStatusNotice) ? (
    <MinimizedStatusNoticeStrip
      hasRightPanel={hasRightPanel}
      notice={activeStatusNotice}
      onExpand={() => setIsStatusNoticeMinimized(false)}
      style={minimizedNoticeStripStyle}
    />
  ) : null;

  return (
    <>
      <div
        className={shellLayoutClassName}
        data-app-shell="true"
        data-optimization-running={["running", "cancelling"].includes(optimizationJobState.status) ? "true" : undefined}
        data-reports-page={isReportsPage ? "true" : undefined}
        style={{
          "--product-update-overlay-height": `${productUpdateOverlayHeight}px`,
          "--shell-right-panel-width": rightPanelWidth,
          "--shell-sidebar-width": sidebarWidth,
        }}
      >
        {hasVisibleProductUpdate ? (
          <div ref={productUpdateOverlayRef} className={productUpdateOverlayClassName}>
            <ProductUpdateBanner className="pointer-events-auto w-full" />
          </div>
        ) : null}

        {isAccessReady && !isRestrictedBillingWorkspace ? (
          <ProductUpdateAnnouncement optimizationStatus={optimizationJobState.status} />
        ) : null}

        {shouldRenderOptimizationProgressCard ? (
          <div
            className="pointer-events-none fixed right-2 z-[80] max-h-[calc(100dvh-1rem)] w-[min(22rem,calc(100vw-1rem))] overflow-y-auto min-[721px]:right-3"
            data-floating-optimization-progress-card="true"
            style={floatingOptimizationProgressStyle}
          >
            <OptimizationProgressCard
              jobState={optimizationJobState}
              onCancel={requestOptimizationCancel}
              onDismiss={dismissOptimizationNotice}
            />
          </div>
        ) : null}

        {todayStalePortalAlert.routes.length > 0 || todayStalePortalAlert.successNotices.length > 0 ? (
          <div className={todayStalePortalOverlayClassName} style={todayStalePortalOverlayStyle}>
            <TodayStalePortalRoutesBanner
              className="pointer-events-auto w-full"
              dispatchDate={todayStalePortalAlert.dispatchDate}
              onDismiss={todayStalePortalAlert.dismissAlert}
              onOpenDispatch={(url) => navigate(url)}
              onPublishRoute={todayStalePortalAlert.publishRoute}
              publishingRouteKey={todayStalePortalAlert.publishingRouteKey}
              routes={todayStalePortalAlert.routes}
              successNotices={todayStalePortalAlert.successNotices}
            />
          </div>
        ) : null}

        {minimizedNoticeStripElement}

        {isCompactShell ? (
          <header className="mobile-shell-header" aria-busy={isPageNavigationPending}>
            <button type="button" className="mobile-shell-menu-button" aria-controls="mobile-navigation" aria-expanded={mobileNavigationOpen} onClick={() => setMobileNavigationOpen(true)}>
              <Menu size={20} aria-hidden="true" /> Menu
            </button>
            <span className="mobile-shell-title">{resolvedHeaderTitle || headerFallback.title || "Accueil"}</span>
            <AskEpiButton compact pageTitle={headerFallback.title} />
            <NotificationCenterButton />
          </header>
        ) : null}
        {shouldRenderTopbar && !isCompactShell ? (
          <header className={topbarClassName} aria-busy={isPageNavigationPending}>
            {usesResultsShellPalette ? (
              <>
                <div className="hidden min-[1281px]:col-start-1 min-[1281px]:block" aria-hidden="true" />

                <div className={topbarMainCellClassName}>
                  <div className="flex min-w-0 items-center gap-3">
                    <ResultsHeaderTitle
                      indicators={headerIndicatorNodes.length > 0 ? headerIndicatorNodes : null}
                      isNavigationPending={isPageNavigationPending}
                      title={resolvedHeaderTitle}
                    />
                    {shouldShowDispatchAlertCaptureBadge || shouldShowReassignmentCaptureBadge ? (
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        {shouldShowReassignmentCaptureBadge ? (
                          <button
                            aria-live="polite"
                            className={DISPATCH_CAPTURE_BADGE_CLASS_NAME}
                            data-dispatch-alert-capture-ignore="true"
                            data-reassignment-diagnostics-capture-status="active"
                            onClick={() => navigate("/captures")}
                            title="Voir toutes les activités"
                            type="button"
                          >
                            <span className="h-1.5 w-1.5 rounded-none bg-accent" aria-hidden="true" />
                            Activité Dispatch
                          </button>
                        ) : null}
                        {shouldShowDispatchAlertCaptureBadge ? (
                          <button
                            aria-live="polite"
                            className={DISPATCH_CAPTURE_BADGE_CLASS_NAME}
                            data-dispatch-alert-capture-ignore="true"
                            data-dispatch-alert-capture-status="active"
                            onClick={() => navigate("/captures?view=errors")}
                            title="Voir les captures d’erreurs"
                            type="button"
                          >
                            <span className="h-1.5 w-1.5 rounded-none bg-accent" aria-hidden="true" />
                            Capture erreurs
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>

                {shouldRenderTopbarRightRail ? (
                  <div className={resultsTopbarRightRailClassName}>
                    <div className="flex flex-nowrap items-center gap-3 whitespace-nowrap">
                      {shouldRenderColorIntensityControl ? <DispatchColorIntensityControl /> : null}
                      <ResultsHeaderStatus status={renderedHeaderStatus} compact={isResultsPage} />

                      {headerActionNodes.length > 0 ? (
                        <div className="flex shrink-0 flex-nowrap justify-end gap-2">
                          {headerActionNodes}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className={topbarMainCellClassName}>
                  <PageSurfaceHeader
                    className={joinClasses([
                      "relative z-1 w-full gap-3",
                      "items-start",
                    ])}
                    copyClassName="grid max-w-[980px] gap-1 pt-0.5"
                    eyebrow={resolvedHeaderEyebrow}
                    eyebrowClassName={APP_SHELL_CLASS_NAMES.headerEyebrow}
                    title={resolvedHeaderTitle}
                    titleAs="h1"
                    titleClassName="m-0 text-balance text-[clamp(1.45rem,2vw,2.05rem)] leading-[1.05] tracking-[-0.035em] max-[720px]:text-[1.7rem]"
                    description={resolvedHeaderSubtitle}
                    descriptionClassName="m-0 max-w-[72ch] text-[0.96rem] leading-[1.55] text-slate-600/95"
                    indicators={headerIndicatorNodes.length > 0 ? headerIndicatorNodes : null}
                    actions={!hasRightPanel && headerActionNodes.length > 0 ? headerActionNodes : null}
                    actionsClassName={topbarActionsClassName}
                  />
                </div>

                {shouldRenderTopbarRightRail ? (
                  <div className={topbarActionRailClassName}>
                    <div className={topbarActionsClassName}>
                      {renderedHeaderStatus ? <ResultsHeaderStatus status={renderedHeaderStatus} /> : null}

                      {headerActionNodes}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </header>
        ) : null}

        <ResponsiveNavigation compact={isCompactShell} open={mobileNavigationOpen} onClose={() => setMobileNavigationOpen(false)}>
        <aside className={sidebarClassName} data-sidebar-collapsed={sidebarCollapsed ? "true" : "false"}>
          {usesResultsShellPalette ? (
            <div className={joinClasses([
              "shell-brand-row mb-1 flex w-full shrink-0 items-center gap-2",
              sidebarCollapsed ? "justify-center px-0 pt-1" : "justify-between px-2.5 pt-1.5",
            ])}>
              {resultsHeaderLead}
              {!isCompactShell ? <ResultsHeaderPanelToggle
                className="shrink-0"
                collapsed={sidebarCollapsed}
                onToggle={() => setSidebarCollapsed((previousState) => !previousState)}
                toggleLabel={sidebarToggleLabel}
              /> : null}
            </div>
          ) : (
            <div className={joinClasses([
              "mb-3 flex flex-col gap-1.5",
              sidebarCollapsed ? "items-center" : "",
            ])}>
              <div className={joinClasses([
                "flex w-full",
                sidebarCollapsed ? "justify-center" : "justify-end px-4 pt-1",
              ])}>
                <button
                  type="button"
                  onClick={() => setSidebarCollapsed((previousState) => !previousState)}
                  className={joinClasses([
                    "sidebar__toggle inline-flex h-9 min-w-9 items-center justify-center rounded-lg border border-slate-300 px-2.5 text-slate-600 shadow-none transition-colors duration-150 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-300 max-[1280px]:hidden",
                  ])}
                  title={sidebarToggleLabel}
                  aria-label={sidebarToggleLabel}
                >
                  {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
                </button>
              </div>
            </div>
          )}

          <nav className={sidebarNavClassName} aria-label="Navigation principale">
            {resolvedNavSections.map((section, index) => (
              <div
                key={section.label || `nav-section-${index}`}
                className="contents"
                data-nav-section={getNavSectionSlug(section, index)}
              >
                {sidebarCollapsed && index > 0 ? (
                  <div
                    className="nav-section-divider hidden min-[1281px]:block"
                    role="presentation"
                    aria-hidden="true"
                  />
                ) : null}
                {section.label ? (
                  (() => {
                    const sectionDescription = resolveNavSectionHoverDescription(section);
                    const sectionTitle = (
                      <p className={joinClasses([
                        "px-4 text-[9px] font-extrabold uppercase tracking-[0.18em] text-slate-400 transition-all duration-200",
                        sidebarCollapsed ? "min-[1281px]:hidden" : "",
                      ])}>
                        {section.label}
                      </p>
                    );

                    if (!sectionDescription || sidebarCollapsed) {
                      return sectionTitle;
                    }

                    return (
                      <HoverCard
                        title={section.label}
                        content={(
                          <NavigationHoverCardContent
                            eyebrow="Section du menu"
                            description={sectionDescription}
                            detail={`${(section.items || []).length} entrée${(section.items || []).length > 1 ? "s" : ""}.`}
                          />
                        )}
                        placement="right"
                        maxWidth={340}
                        hoverOnly delay={MAIN_NAV_HOVERCARD_DELAY_MS}
                      >
                        {sectionTitle}
                      </HoverCard>
                    );
                  })()
                ) : null}
                {(section.items || []).map((item) => {
                  const itemKey = getNavItemKey(item, `nav-section-${index}-item`);

                  return (
                    <NavItem
                      key={itemKey}
                      item={item}
                      sectionLabel={section.label || ""}
                      sidebarCollapsed={sidebarCollapsed}
                      isOpen={openNavItemKey === itemKey}
                      onNavigate={(event, navItem) => {
                        handleNavItemNavigate(event, navItem);
                        if (isCompactShell && navItem?.is_disabled !== true) setMobileNavigationOpen(false);
                      }}
                      onToggle={() => toggleNavItem(itemKey)}
                    />
                  );
                })}

                {typeof onManualSignOut === "function" && (section.label || "").trim().toLowerCase() === "admin" ? (
                  <div className="px-0 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        void onManualSignOut();
                      }}
                      disabled={isSigningOut}
                      className={sidebarSignOutButtonClassName}
                      aria-label="Déconnexion"
                      title="Déconnexion"
                    >
                      <span className="nav-item__icon flex items-center justify-center transition-all duration-200" aria-hidden="true">
                        {isSigningOut ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <LogOut size={17} />}
                      </span>
                      <span className={joinClasses(["min-w-0 flex-1 text-[0.9rem] font-medium", sidebarCollapsed ? "min-[1281px]:hidden" : ""])}>
                        {isSigningOut ? "Déconnexion..." : "Déconnexion"}
                      </span>
                    </button>
                  </div>
                ) : null}
              </div>
            ))}

            {typeof onManualSignOut === "function" && !hasAdminNavSection ? (
              <div className="px-0 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    void onManualSignOut();
                  }}
                  disabled={isSigningOut}
                  className={sidebarSignOutButtonClassName}
                  aria-label="Déconnexion"
                  title="Déconnexion"
                >
                  <span className="nav-item__icon flex items-center justify-center transition-all duration-200" aria-hidden="true">
                    {isSigningOut ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <LogOut size={17} />}
                  </span>
                  <span className={joinClasses(["min-w-0 flex-1 text-[0.9rem] font-medium", sidebarCollapsed ? "min-[1281px]:hidden" : ""])}>
                    {isSigningOut ? "Déconnexion..." : "Déconnexion"}
                  </span>
                </button>
              </div>
            ) : null}
          </nav>

          {brand.identity_name || brand.logo_url ? (
          <div className={joinClasses([
            sidebarSectionClassName,
            "mt-auto shrink-0 pt-2",
          ])}>
            <div className={joinClasses([
              "shell-identity mx-auto grid w-full max-w-41 gap-1.5 rounded-[4px] border p-2.5 opacity-100 shadow-shell-inset-soft transition-all duration-200",
              usesResultsShellPalette
                ? "border-shell-identity-panel-results-border bg-surface-identity-panel-results"
                : "border-shell-identity-panel-default-border bg-surface-identity-panel-default",
              sidebarCollapsed ? "min-[1281px]:hidden" : "",
            ])}>
              <p className="mb-0.5 text-[9px] font-extrabold uppercase tracking-[0.18em] text-slate-400">{brand.identity_label}</p>
              <Link
                className="grid gap-1.5 rounded-lg focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-slate-300/70"
                to="/?fromApp=1"
                aria-label={`Retour à l'accueil public ${brand.identity_name || ""}`.trim()}
              >
                {brand.logo_url ? (
                <div className={joinClasses([
                  "flex min-h-12 items-center justify-center rounded-lg border px-1.5 py-1.5 shadow-shell-inset",
                  usesResultsShellPalette
                    ? "border-shell-identity-logo-results-border bg-surface-identity-logo-results"
                    : "border-shell-identity-logo-default-border bg-white",
                ])}>
                  <div className="relative mx-auto flex w-full max-w-24 items-center justify-center overflow-hidden rounded-[10px] border border-[rgb(255_255_255/0.14)] bg-accent px-2 py-1.5 shadow-[0_8px_20px_rgb(15_92_87/0.12)]">
                    <img
                      className={joinClasses([
                        "relative h-auto w-full object-contain drop-shadow-[0_4px_12px_rgb(8_19_22/0.35)]",
                        isResultsPage ? "max-h-10 max-w-24" : "max-h-10 max-w-24",
                      ])}
                      src={brand.logo_url}
                      alt={brand.identity_name || ""}
                      loading={isResultsPage ? "eager" : "lazy"}
                    />
                  </div>
                </div>
                ) : null}
                {brand.identity_name ? (
                <div className="grid gap-1">
                  <strong className="whitespace-nowrap text-center text-[0.68rem] leading-[1.15] text-slate-700">{brand.identity_name}</strong>
                </div>
                ) : null}
              </Link>
            </div>
          </div>
          ) : null}
        </aside>
        </ResponsiveNavigation>

        <div className={shellMainClassName}>
          {isPageNavigationPending ? (
            <p className="sr-only" aria-live="polite">
              {navigationStatusLabel}
            </p>
          ) : null}

          {fullscreenNoticeMountNode instanceof HTMLElement
            ? createPortal(noticeElement, fullscreenNoticeMountNode)
            : noticeElement}

          <PreferencesDelegationBanner />

          <main className={contentClassName}>
            {resolvedChildren}
          </main>
        </div>

        {hasRightPanel ? (
          <RightPanel
            collapsed={rightPanelCollapsed}
            collapseToggle={(
              <ResultsHeaderPanelToggle
                className="max-[1280px]:hidden"
                collapsed={rightPanelCollapsed}
                onToggle={() => setRightPanelCollapsed((previousState) => {
                  storeRightPanelCollapsed(!previousState);
                  return !previousState;
                })}
                side="right"
                toggleLabel={rightPanelToggleLabel}
              />
            )}
            data={shellData?.right_panel ?? null}
          />
        ) : null}
      </div>
    </>
  );
}
